<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import PropertyTransactionsAPI from 'dashboard/api/propertyTransactions';
import ReservationHoldsAPI from 'dashboard/api/reservationHolds';
import ImovelAPI from 'dashboard/api/imoveis';
import { useMapGetter } from 'dashboard/composables/store';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import OfferDetailsAlert from 'dashboard/components-next/offer/OfferDetailsAlert.vue';
import OfferOverview from 'dashboard/components-next/offer/OfferOverview.vue';
import OfferStatusList from 'dashboard/components-next/offer/OfferStatusList.vue';
import OfferCard from '../components/OfferCard.vue';
import OfferCardsHeader from '../components/OfferCardsHeader.vue';
import OfferDetailsHeader from '../components/OfferDetailsHeader.vue';
import OfferSummaryCard from '../components/OfferSummaryCard.vue';
import OfferBucketTabs from '../components/OfferBucketTabs.vue';
import RentProgressPhaseTabs from '../components/RentProgressPhaseTabs.vue';
import OfferListFilters from '../components/OfferListFilters.vue';
import OfferListSkeleton from '../components/OfferListSkeleton.vue';
import OfferListEmptyState from '../components/OfferListEmptyState.vue';
import OfferDetailsValuesTable from '../components/OfferDetailsValuesTable.vue';
import OfferDetailsSolicitations from '../components/OfferDetailsSolicitations.vue';
import OfferOperationalActivityPanel from '../components/OfferOperationalActivityPanel.vue';
import OfferDetailsContractSection from '../components/OfferDetailsContractSection.vue';
import OfferDetailsPresentationCards from '../components/OfferDetailsPresentationCards.vue';
import OfferKycPanel from '../components/OfferKycPanel.vue';
import OfferProgressTimeline from '../components/OfferProgressTimeline.vue';
import OfferPartnersCard from '../components/OfferPartnersCard.vue';
import OfferDetailsFooterActions from '../components/OfferDetailsFooterActions.vue';
import OfferCreateBuyerStep from '../components/OfferCreateBuyerStep.vue';
import OfferCreatePropertyStep from '../components/OfferCreatePropertyStep.vue';
import OfferCreateReviewSection from '../components/OfferCreateReviewSection.vue';
import OfferCreateStepper from '../components/OfferCreateStepper.vue';
import OfferCreateValuesStep from '../components/OfferCreateValuesStep.vue';
import OfferDecisionModals from '../components/OfferDecisionModals.vue';
import OfferCounterModal from '../components/OfferCounterModal.vue';
import OfferEditModal from '../components/OfferEditModal.vue';
import OfferFilterDrawers from '../components/OfferFilterDrawers.vue';
import OfferContractDrawer from '../components/OfferContractDrawer.vue';
import PublicLinkTokenPanel from '../components/PublicLinkTokenPanel.vue';
import KycDiligenceChecklistPanel from '../components/KycDiligenceChecklistPanel.vue';
import TransactionChecklistPanel from '../components/TransactionChecklistPanel.vue';
import ReservationHoldPanel from '../components/ReservationHoldPanel.vue';
import { useAlert, useTracking } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { trackQuintoEvent } from 'dashboard/helper/lais/trackingEvents';
import {
  getOfferBusinessContextLabel,
  getOfferKindLabel,
  getOfferStatusClass,
  getOfferStatusLabel,
} from 'dashboard/helper/lais/offerPresenter';
import {
  activityFilterLabel as buildActivityFilterLabel,
  activityFilterValidationError,
  normalizeActivityFilter,
} from '../utils/offerFilterUtils';
import {
  buildNativeOfferListParams,
  REFERENCE_DEFAULT_OFFER_LIST_SIZE,
  REFERENCE_DEFAULT_OFFER_PAGE_ORIGIN,
} from '../utils/referenceOfferLogic';
import {
  OFFER_PAGE_TRACKING_EVENTS,
  offerDetailsChatPayload,
  offerPageTrackingEnvelope,
  offerPageViewPayload,
  offerRecentActivityPayload,
  offerTabPayload,
  offerTypeFilterPayload,
} from '../utils/offerPageTracking';
import {
  RENT_PROGRESS_CONTRACT_PERIODS,
  RENT_PROGRESS_CONTRACT_STATUSES,
  RENT_PROGRESS_DOCUMENTATION_PERIODS,
  RENT_PROGRESS_PHASES,
  RENT_PROGRESS_PHASE_TABS,
  filterOffersByRentProgressContractFilters,
  filterOffersByRentProgressPhase,
  normalizeRentProgressContractPeriod,
  normalizeRentProgressContractStatus,
  normalizeRentProgressDocumentationPeriod,
  normalizeRentProgressPhase,
  rentProgressCardActions,
  rentProgressNegotiationSummaries,
  rentProgressContractFilterLabel,
  rentProgressDocumentationFilterLabel,
  rentProgressPhaseEmptyState,
  rentProgressPhaseHeader,
  rentProgressPhaseLoadMoreLabel,
  rentProgressPhaseSupportText,
} from '../utils/rentProgressPhasePresenter';
import { offerDecisionState } from '../utils/offerStatusActions';
import {
  activeKycVerificationForOffer,
  canCreateKycVerificationForOffer,
  isRetryableKycVerification,
  kycVerificationsForOffer,
} from '../utils/kycOfferHistory';
import {
  contractOperationErrorMessage,
  normalizeContractCancelReason,
} from '../utils/offerContractDetails';
import {
  buildOfferRouteSyncTarget,
  compactQuery,
  initialActivityFilterFromRoute,
  initialContactIdFromRoute,
  initialNegotiationStatusFromRoute,
  initialOfferBucketFromRoute,
  initialPropertyIdFromRoute,
  initialRentProgressContractPeriodFromRoute,
  initialRentProgressContractStatusFromRoute,
  initialRentProgressDocumentationPeriodFromRoute,
  initialRentProgressPhaseFromRoute,
  initialSortFromRoute,
  normalizedFromList,
  normalizedRouteText,
  offerDetailsRouteIdentifier,
  offerIdentifierMatches,
  offerRouteWatchSources,
  routeString,
} from '../utils/offerRouteState';
import {
  createSteps,
  customSolicitationTypeOptions,
  declineReasonOptions,
  defaultProposalForm,
  isFinancingPayment,
  offerBuckets,
  offerKindOptions,
  offerKindValues,
  offerSortOptions,
  offerSortValues,
  paymentKindValues,
  paymentLabel,
  paymentNeedsFgts,
  paymentOptions,
  rentalDeclineReasons,
} from '../utils/offerFormOptions';
import {
  clientInitials,
  formatBRL,
  formatFormBRL,
} from '../utils/offerFormatters';
import {
  materializedExternalIds,
  normalizedExternalItem,
} from '../utils/offerCatalogItem';
import {
  buyerLabel,
  contractForOffer,
  customSolicitations,
  decisionPriceSummary,
  isRentalOffer,
  offerAmountLabel,
  offerAcceptedSummary,
  offerCanceledReason,
  offerContactId,
  offerConversationId,
  offerDecision,
  offerImovelId,
  offerReference,
  offerStatusDetail,
  offerSummary,
  ownerWhatsappHrefForOffer,
  valueRowsForOffer,
} from '../utils/offerReadModel';
import { kycProviderErrorMessage } from '../utils/offerContractPresenter';
import {
  offerProgressAlert,
  offerProgressSteps,
} from '../utils/offerProgressSteps';
import {
  apiErrorMessage,
  isConflictError,
  isUnavailableFeatureError,
} from '../utils/offerRequestErrors';
import { useOfferProposalPricing } from '../composables/useOfferProposalPricing';

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const { track } = useTracking();
const { isAdmin } = useAdmin();

const initialContextFromRoute = () => {
  const context = normalizedRouteText(
    route.query.business_context ||
      route.query.context ||
      route.query.tipo ||
      route.query.finalidade
  );
  const path = normalizedRouteText(route.path);
  if (
    ['purchase', 'sale', 'venda'].includes(context) ||
    path.includes('/propostas/venda') ||
    path.includes('/sale-offers')
  ) {
    return 'purchase';
  }
  if (
    ['rental', 'rent', 'aluguel'].includes(context) ||
    path.includes('/propostas/aluguel') ||
    path.includes('/rent-offers')
  ) {
    return 'rental';
  }
  return 'all';
};

const offers = ref([]);
const loading = ref(false);
const loadError = ref(false);
const loadErrorMessage = ref('');
const offerBucket = ref(initialOfferBucketFromRoute(route));
const rentProgressPhase = ref(initialRentProgressPhaseFromRoute(route));
const rentProgressContractPeriod = ref(
  initialRentProgressContractPeriodFromRoute(route)
);
const rentProgressContractStatus = ref(
  initialRentProgressContractStatusFromRoute(route)
);
const rentProgressDocumentationPeriod = ref(
  initialRentProgressDocumentationPeriodFromRoute(route)
);
const negotiationStatus = ref(initialNegotiationStatusFromRoute(route));
const filterKind = ref(
  normalizedFromList(route.query.kind, offerKindValues, 'all')
);
const filterContext = ref(initialContextFromRoute());
const filterPaymentKind = ref(
  normalizedFromList(route.query.payment_kind, paymentKindValues, 'all')
);
const initialActivityFilter = initialActivityFilterFromRoute(route);
const filterActivityMonths = ref(initialActivityFilter.months);
const filterActivityStartDate = ref(initialActivityFilter.startDate);
const filterActivityEndDate = ref(initialActivityFilter.endDate);
const searchTerm = ref(routeString(route.query.q));
const contactIdFilter = ref(initialContactIdFromRoute(route));
const propertyIdFilter = ref(initialPropertyIdFromRoute(route));
let isApplyingRouteState = false;
let isSyncingRouteState = false;
let isSelectingClientSearchOption = false;
let searchDebounceTimer;
const currentOfferSize = ref(REFERENCE_DEFAULT_OFFER_LIST_SIZE);
const totalCount = ref(0);
const offerSkeletonCards = Array.from({ length: 5 }, (_, index) => index);

const showCreateModal = ref(false);
const showDetailsModal = ref(false);
const showOfferTypeDrawer = ref(false);
const showPaymentKindDrawer = ref(false);
const showActivityDrawer = ref(false);
const showContractFiltersDrawer = ref(false);
const showDocumentationFiltersDrawer = ref(false);
const showContractDrawer = ref(false);
const showAcceptModal = ref(false);
const showAcceptedFeedbackModal = ref(false);
const showDeclinedFeedbackModal = ref(false);
const showCreateSuccessModal = ref(false);
const showDeclineModal = ref(false);
const showCounterModal = ref(false);
const showEditModal = ref(false);
const counterConflict = ref(false);
const saving = ref(false);
const propertyMaterializing = ref(false);
const offerAction = ref('');
const editError = ref('');
const contractAction = ref('');
const publicLinkAction = ref('');
// The panel renders this inline and keeps its confirmation modal open until the
// operator resolves it — a transient toast alone loses the backend reason.
const publicLinkError = ref('');
const kycAction = ref('');
const createStep = ref('property');
const catalogMode = ref('portfolio');
const catalogQuery = ref('');
const selectedCatalogItem = ref(null);
const selectedCatalogMode = ref('');
const selectedRequesterContact = ref(null);
const requesterContactQuery = ref('');
const selectedOffer = ref(null);
const operationalContextLoading = ref(false);
const operationalContextError = ref('');
const selectedContractProviderStatus = ref(null);
const createdOffer = ref(null);
const offerKindDraft = ref(filterKind.value);
const sortBy = ref(initialSortFromRoute(route, offerSortValues));
const paymentKindDraft = ref(filterPaymentKind.value);
const activityDraft = ref({ ...initialActivityFilter });
const rentProgressContractPeriodDraft = ref(rentProgressContractPeriod.value);
const rentProgressContractStatusDraft = ref(rentProgressContractStatus.value);
const rentProgressDocumentationPeriodDraft = ref(
  rentProgressDocumentationPeriod.value
);
const activityFilterError = ref('');
// Closing surfaces migrated from the retired property_offers detail page.
const selectedTransaction = ref(null);
const transactionLoading = ref(false);
const transactionUnavailable = ref(false);
const checklistActionInFlight = ref('');
const checklistActionError = ref('');
const selectedDiligenceGate = ref(null);
const diligenceLoading = ref(false);
const diligenceUnavailable = ref(false);
const diligenceReviewInFlight = ref(false);
const diligenceReviewError = ref('');
const reservationActionInFlight = ref('');
const reservationError = ref('');
const reservationUnavailable = ref(false);
const offerLinkAction = ref('');
// Re-stamped every time the details drawer opens: stable while the operator
// works on one offer (an item must not silently flip to "overdue" mid-action),
// but never frozen for the whole session — this index page stays mounted.
const referenceNow = ref(new Date().toISOString());
const contractForm = ref({
  pdf_url: '',
  template_id: '',
});
const contractCancelReason = ref('');
const contractResultMessage = ref('');
const contractActionErrorMessage = ref('');

const trackOfferEvent = (event, properties = {}) => {
  try {
    trackQuintoEvent(track, event, {
      pageName: 'offer_page',
      domain: 'offers',
      status_bucket: offerBucket.value,
      kind: filterKind.value,
      sort_by: sortBy.value,
      ...properties,
    });
  } catch {
    // Tracking must not block offer operations.
  }
};

const kycForm = ref({
  document_type: 'CNH',
  consent_accepted: false,
});
const kycCapturePayload = ref({
  selfie_base64: '',
  document_base64: '',
  document_back_base64: '',
});
const showKycCapture = ref(false);
const proposalForm = ref(defaultProposalForm());
const declineForm = ref({
  reason: 'conditions_not_agreed',
  disable_listing: false,
  notes: '',
});

const {
  paymentRecalculating,
  isPurchaseProposalForm,
  isRentalProposalForm,
  proposalAmountNumber,
  selectedCatalogImovelId,
  isFinancingProposalPayment,
  isSfhEligibleForProposal,
  showSfhOption,
  sfhLimitLabel,
  paymentBreakdownDifference,
  paymentBreakdownHint,
  purchasePaymentInfo,
  purchasePaymentRows,
  financingInstallmentLabel,
  syncPaymentBreakdownDefaultsNow,
  schedulePaymentBreakdownDefaults,
  recalculatePaymentFees,
  rentalAdminFeePercentage,
  rentalAdminFeeValue,
  rentalOwnerReceiveValue,
  rentalResidentsSituation,
  selectedCatalogSummary,
  proposalDeadlinePreview,
  applyCatalogItemToProposal,
  createPayload,
} = useOfferProposalPricing({
  proposalForm,
  selectedCatalogItem,
  selectedCatalogMode,
  catalogMode,
});

const rentProgressPhases = RENT_PROGRESS_PHASE_TABS;
const rentProgressContractPeriodOptions = RENT_PROGRESS_CONTRACT_PERIODS;
const rentProgressContractStatusOptions = RENT_PROGRESS_CONTRACT_STATUSES;
const rentProgressDocumentationPeriodOptions =
  RENT_PROGRESS_DOCUMENTATION_PERIODS;

const createStepIndex = computed(() =>
  Math.max(
    0,
    createSteps.findIndex(step => step.value === createStep.value)
  )
);

const pageTitle = computed(() => 'Propostas');

// Explains which slice of the funnel the operator is looking at, so the header
// stops being a bare title when a bucket/phase filter is active.
const pageHelper = computed(() => {
  if (loading.value || loadError.value) return '';
  if (offerBucket.value === 'finished') {
    return 'Propostas aceitas e negociações concluídas.';
  }
  if (offerBucket.value === 'canceled') {
    return 'Propostas recusadas, retiradas ou expiradas.';
  }
  if (negotiationStatus.value === 'submitted') {
    return 'Propostas aguardando resposta do proprietário.';
  }
  return 'Propostas em andamento, da chegada ao contrato assinado.';
});

const offerPageOrigin = () =>
  routeString(
    route.query.origin || route.query.from,
    REFERENCE_DEFAULT_OFFER_PAGE_ORIGIN
  );

const syncRouteState = () => {
  const target = buildOfferRouteSyncTarget({
    route,
    accountId: accountId.value,
    offerBucket: offerBucket.value,
    rentProgressPhase: rentProgressPhase.value,
    rentProgressContractPeriod: rentProgressContractPeriod.value,
    rentProgressContractStatus: rentProgressContractStatus.value,
    rentProgressDocumentationPeriod: rentProgressDocumentationPeriod.value,
    negotiationStatus: negotiationStatus.value,
    filterContext: filterContext.value,
    filterKind: filterKind.value,
    filterPaymentKind: filterPaymentKind.value,
    filterActivityMonths: filterActivityMonths.value,
    filterActivityStartDate: filterActivityStartDate.value,
    filterActivityEndDate: filterActivityEndDate.value,
    searchTerm: searchTerm.value,
    entityFilters: {
      contact_id: contactIdFilter.value,
      imovel_id: propertyIdFilter.value,
    },
    sortBy: sortBy.value,
  });

  isSyncingRouteState = true;
  router
    .replace(target)
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingRouteState = false;
      }, 0);
    });
};

const applyRouteState = () => {
  offerBucket.value = initialOfferBucketFromRoute(route);
  rentProgressPhase.value = initialRentProgressPhaseFromRoute(route);
  rentProgressContractPeriod.value =
    initialRentProgressContractPeriodFromRoute(route);
  rentProgressContractStatus.value =
    initialRentProgressContractStatusFromRoute(route);
  rentProgressDocumentationPeriod.value =
    initialRentProgressDocumentationPeriodFromRoute(route);
  negotiationStatus.value = initialNegotiationStatusFromRoute(route);
  filterKind.value = normalizedFromList(
    route.query.kind,
    offerKindValues,
    'all'
  );
  filterContext.value = initialContextFromRoute();
  filterPaymentKind.value = normalizedFromList(
    route.query.payment_kind,
    paymentKindValues,
    'all'
  );
  sortBy.value = initialSortFromRoute(route, offerSortValues);
  const activityFilter = initialActivityFilterFromRoute(route);
  filterActivityMonths.value = activityFilter.months;
  filterActivityStartDate.value = activityFilter.startDate;
  filterActivityEndDate.value = activityFilter.endDate;
  searchTerm.value = routeString(route.query.q);
  contactIdFilter.value = initialContactIdFromRoute(route);
  propertyIdFilter.value = initialPropertyIdFromRoute(route);
  currentOfferSize.value = REFERENCE_DEFAULT_OFFER_LIST_SIZE;
};

let syncOfferDetailsFromRoute;
let openOfferDetails;
let refreshSelectedOfferDeepState = async () => {};
let refreshSelectedContractState = async () => {};

const fetchOffers = async ({ syncRoute = true } = {}) => {
  loading.value = true;
  try {
    if (syncRoute) {
      syncRouteState();
    }
    const { data } = await PropertyOffersAPI.get(
      buildNativeOfferListParams({
        currentOfferSize: currentOfferSize.value,
        sortBy: sortBy.value,
        statusBucket: offerBucket.value,
        negotiationStatus: negotiationStatus.value,
        kind: filterKind.value === 'all' ? '' : filterKind.value,
        businessContext: filterContext.value,
        paymentKind: filterPaymentKind.value,
        activityFilter: {
          months: filterActivityMonths.value,
          startDate: filterActivityStartDate.value,
          endDate: filterActivityEndDate.value,
        },
        rentProgressPhase:
          offerBucket.value === 'in_negotiation' ? rentProgressPhase.value : '',
        contractPeriod:
          offerBucket.value === 'in_negotiation' &&
          rentProgressPhase.value === 'contract'
            ? rentProgressContractPeriod.value
            : '',
        contractStatus:
          offerBucket.value === 'in_negotiation' &&
          rentProgressPhase.value === 'contract'
            ? rentProgressContractStatus.value
            : '',
        documentationPeriod:
          offerBucket.value === 'in_negotiation' &&
          rentProgressPhase.value === 'documentation'
            ? rentProgressDocumentationPeriod.value
            : '',
        searchTerm: searchTerm.value,
        contactId: contactIdFilter.value,
        imovelId: propertyIdFilter.value,
      })
    );
    offers.value = data.data || [];
    totalCount.value = data.meta?.count || offers.value.length;
    loadError.value = false;
    loadErrorMessage.value = '';
    await syncOfferDetailsFromRoute();
    trackOfferEvent(
      OFFER_PAGE_TRACKING_EVENTS.OFFER_PAGE_VIEW,
      offerPageViewPayload({
        origin: offerPageOrigin(),
        size: currentOfferSize.value,
        totalCount: totalCount.value,
        visibleCount: offers.value.length,
      })
    );
  } catch (error) {
    loadErrorMessage.value = apiErrorMessage(
      error,
      'Não foi possível carregar as propostas.'
    );
    useAlert(loadErrorMessage.value);
    offers.value = [];
    totalCount.value = 0;
    loadError.value = true;
  } finally {
    loading.value = false;
  }
};

const resetOfferListSize = () => {
  currentOfferSize.value = REFERENCE_DEFAULT_OFFER_LIST_SIZE;
};

const refreshOffers = () => {
  if (loadError.value) {
    trackOfferEvent(
      OFFER_PAGE_TRACKING_EVENTS.OFFER_ERROR_REFRESH_BUTTON_CLICKED,
      offerPageTrackingEnvelope()
    );
  } else {
    trackOfferEvent(
      OFFER_PAGE_TRACKING_EVENTS.OFFER_REFRESH_BUTTON_CLICKED,
      offerPageTrackingEnvelope()
    );
  }
  fetchOffers({ syncRoute: false });
};

const trackRentProgressPageView = phase => {
  const tabName = normalizeRentProgressPhase(phase);
  trackOfferEvent('rent_progress_status_page_viewed', {
    domain: 'rent_progress',
    tab_name: tabName,
    rent_progress_status: tabName,
  });
};

onMounted(() => {
  if (offerBucket.value === 'in_negotiation') {
    trackRentProgressPageView(rentProgressPhase.value);
  }
  fetchOffers();
});

watch(searchTerm, () => {
  if (isApplyingRouteState) return;

  if (!isSelectingClientSearchOption) {
    contactIdFilter.value = '';
  }
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    resetOfferListSize();
    fetchOffers();
  }, 350);
});

watch(
  () => offerRouteWatchSources(route),
  async () => {
    if (isSyncingRouteState) return;

    isApplyingRouteState = true;
    clearTimeout(searchDebounceTimer);
    applyRouteState();
    isApplyingRouteState = false;
    await fetchOffers({ syncRoute: false });
  }
);

onBeforeUnmount(() => {
  clearTimeout(searchDebounceTimer);
});

const selectOfferBucket = value => {
  offerBucket.value = value;
  if (value !== 'in_negotiation') {
    rentProgressPhase.value = 'negotiation';
  }
  negotiationStatus.value = 'all';
  resetOfferListSize();
  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_TAB_CLICKED,
    offerTabPayload(value)
  );
  fetchOffers();
};

const selectRentProgressPhase = value => {
  rentProgressPhase.value = normalizeRentProgressPhase(value);
  resetOfferListSize();
  trackRentProgressPageView(rentProgressPhase.value);
  trackOfferEvent('rent_progress_tab_clicked', {
    rent_progress_phase: rentProgressPhase.value,
  });
  fetchOffers();
};

const applyFilters = () => {
  resetOfferListSize();
  fetchOffers();
};

const openOfferTypeDrawer = () => {
  offerKindDraft.value = filterKind.value;
  showOfferTypeDrawer.value = true;
};

const applyOfferTypeDrawer = () => {
  filterKind.value = offerKindDraft.value || 'all';
  showOfferTypeDrawer.value = false;
  if (filterKind.value !== 'all') {
    trackOfferEvent(
      OFFER_PAGE_TRACKING_EVENTS.OFFER_QUICK_FILTERS_CLICKED,
      offerTypeFilterPayload(filterKind.value)
    );
  }
  applyFilters();
};

const clearOfferTypeDrawer = () => {
  offerKindDraft.value = 'all';
  filterKind.value = 'all';
  showOfferTypeDrawer.value = false;
  applyFilters();
};

const openPaymentKindDrawer = () => {
  paymentKindDraft.value = filterPaymentKind.value;
  showPaymentKindDrawer.value = true;
};

const applyPaymentKindDrawer = () => {
  filterPaymentKind.value = paymentKindDraft.value || 'all';
  showPaymentKindDrawer.value = false;
  if (filterPaymentKind.value !== 'all') {
    trackOfferEvent(
      OFFER_PAGE_TRACKING_EVENTS.OFFER_QUICK_FILTERS_CLICKED,
      offerTypeFilterPayload(filterPaymentKind.value)
    );
  }
  applyFilters();
};

const clearPaymentKindDrawer = () => {
  paymentKindDraft.value = 'all';
  filterPaymentKind.value = 'all';
  showPaymentKindDrawer.value = false;
  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_FILTERS_TYPE_CLEAR_CLICKED,
    offerPageTrackingEnvelope()
  );
  applyFilters();
};

const currentActivityFilter = () =>
  normalizeActivityFilter({
    months: filterActivityMonths.value,
    startDate: filterActivityStartDate.value,
    endDate: filterActivityEndDate.value,
  });

const openActivityDrawer = () => {
  activityDraft.value = { ...currentActivityFilter() };
  activityFilterError.value = '';
  showActivityDrawer.value = true;
};

const selectActivityPreset = months => {
  activityDraft.value = {
    months: String(months),
    startDate: '',
    endDate: '',
  };
  activityFilterError.value = '';
};

const selectActivityCustomRange = () => {
  activityDraft.value = {
    ...activityDraft.value,
    months: '',
  };
  activityFilterError.value = '';
};

const applyActivityDrawer = () => {
  const draft = activityDraft.value;
  const hasCustomInput = draft.startDate || draft.endDate;
  const normalized = normalizeActivityFilter(draft);
  const validationError = activityFilterValidationError(draft);

  if (!normalized.months && hasCustomInput && validationError) {
    activityFilterError.value = validationError;
    return;
  }

  filterActivityMonths.value = normalized.months;
  filterActivityStartDate.value = normalized.startDate;
  filterActivityEndDate.value = normalized.endDate;
  activityFilterError.value = '';
  showActivityDrawer.value = false;
  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_FILTERS_RECENT_ACTIVITIES_APPLY_CLICKED,
    offerRecentActivityPayload(normalized)
  );
  applyFilters();
};

const clearActivityDrawer = () => {
  activityDraft.value = { months: '', startDate: '', endDate: '' };
  filterActivityMonths.value = '';
  filterActivityStartDate.value = '';
  filterActivityEndDate.value = '';
  activityFilterError.value = '';
  showActivityDrawer.value = false;
  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_FILTERS_RECENT_ACTIVITIES_CLEAR_CLICKED,
    offerPageTrackingEnvelope()
  );
  applyFilters();
};

const clearPropertyFilter = () => {
  propertyIdFilter.value = '';
  resetOfferListSize();
  applyFilters();
};

const openContractFiltersDrawer = () => {
  trackOfferEvent('rent_progress_status_filter_clicked', {
    domain: 'rent_progress',
    filter: 'contract',
    rent_progress_phase: rentProgressPhase.value,
  });
  rentProgressContractPeriodDraft.value = rentProgressContractPeriod.value;
  rentProgressContractStatusDraft.value = rentProgressContractStatus.value;
  showContractFiltersDrawer.value = true;
};

const applyContractFiltersDrawer = () => {
  rentProgressContractPeriod.value = normalizeRentProgressContractPeriod(
    rentProgressContractPeriodDraft.value
  );
  rentProgressContractStatus.value = normalizeRentProgressContractStatus(
    rentProgressContractStatusDraft.value
  );
  showContractFiltersDrawer.value = false;
  resetOfferListSize();
  trackOfferEvent('rent_progress_status_filter_submitted', {
    domain: 'rent_progress',
    filter: 'contract',
    rent_progress_phase: rentProgressPhase.value,
  });
  trackOfferEvent('rent_progress_contract_filters_apply_clicked', {
    domain: 'rent_progress',
    rent_progress_phase: rentProgressPhase.value,
    contract_period: rentProgressContractPeriod.value,
    contract_status: rentProgressContractStatus.value,
  });
  fetchOffers();
};

const clearContractFiltersDrawer = () => {
  rentProgressContractPeriodDraft.value = 'LAST_30_DAYS';
  rentProgressContractStatusDraft.value = 'ALL';
  rentProgressContractPeriod.value = 'LAST_30_DAYS';
  rentProgressContractStatus.value = 'ALL';
  showContractFiltersDrawer.value = false;
  resetOfferListSize();
  trackOfferEvent('rent_progress_contract_filters_clear_clicked', {
    domain: 'rent_progress',
    rent_progress_phase: rentProgressPhase.value,
    contract_period: rentProgressContractPeriod.value,
    contract_status: rentProgressContractStatus.value,
  });
  fetchOffers();
};

const openDocumentationFiltersDrawer = () => {
  trackOfferEvent('rent_progress_status_filter_clicked', {
    domain: 'rent_progress',
    filter: 'documentation',
    rent_progress_phase: rentProgressPhase.value,
  });
  rentProgressDocumentationPeriodDraft.value =
    rentProgressDocumentationPeriod.value;
  showDocumentationFiltersDrawer.value = true;
};

const applyDocumentationFiltersDrawer = () => {
  rentProgressDocumentationPeriod.value =
    normalizeRentProgressDocumentationPeriod(
      rentProgressDocumentationPeriodDraft.value
    );
  showDocumentationFiltersDrawer.value = false;
  resetOfferListSize();
  trackOfferEvent('rent_progress_status_filter_submitted', {
    domain: 'rent_progress',
    filter: 'documentation',
    rent_progress_phase: rentProgressPhase.value,
  });
  trackOfferEvent('rent_progress_documentation_filters_apply_clicked', {
    domain: 'rent_progress',
    rent_progress_phase: rentProgressPhase.value,
    documentation_period: rentProgressDocumentationPeriod.value,
  });
  fetchOffers();
};

const clearDocumentationFiltersDrawer = () => {
  rentProgressDocumentationPeriodDraft.value = 'LAST_30_DAYS';
  rentProgressDocumentationPeriod.value = 'LAST_30_DAYS';
  showDocumentationFiltersDrawer.value = false;
  resetOfferListSize();
  trackOfferEvent('rent_progress_documentation_filters_clear_clicked', {
    domain: 'rent_progress',
    rent_progress_phase: rentProgressPhase.value,
    documentation_period: rentProgressDocumentationPeriod.value,
  });
  fetchOffers();
};

const handleOfferSortChange = () => {
  applyFilters();
};

const baseVisibleOffers = computed(() => offers.value);

const isRentProgressPhaseEnabled = computed(
  () => offerBucket.value === 'in_negotiation'
);

const rentProgressPhaseOffers = computed(() =>
  isRentProgressPhaseEnabled.value
    ? filterOffersByRentProgressPhase(
        baseVisibleOffers.value,
        rentProgressPhase.value
      )
    : baseVisibleOffers.value
);

const visibleOffers = computed(() => {
  if (
    !isRentProgressPhaseEnabled.value ||
    rentProgressPhase.value !== 'contract'
  ) {
    return rentProgressPhaseOffers.value;
  }

  return filterOffersByRentProgressContractFilters(
    rentProgressPhaseOffers.value,
    {
      status: rentProgressContractStatus.value,
    }
  );
});

const displayCount = computed(() =>
  isRentProgressPhaseEnabled.value
    ? visibleOffers.value.length
    : totalCount.value
);

const isLastOfferPage = computed(
  () => baseVisibleOffers.value.length >= totalCount.value
);

const loadMoreOffers = () => {
  if (isLastOfferPage.value) return;
  currentOfferSize.value += REFERENCE_DEFAULT_OFFER_LIST_SIZE;
  fetchOffers();
};

const hasNoVisibleOffers = computed(() => visibleOffers.value.length === 0);

// The list can be empty only because the page-level refinement filtered out the
// page we already hold. In that case "load more" is the useful action, not a
// retry — offer it inside the empty state instead of stranding the operator.
const canLoadMoreFromEmptyState = computed(
  () =>
    !loading.value &&
    !loadError.value &&
    baseVisibleOffers.value.length > 0 &&
    !isLastOfferPage.value
);

const hasActiveOfferFilters = computed(
  () =>
    Boolean(searchTerm.value.trim()) ||
    Boolean(contactIdFilter.value) ||
    Boolean(propertyIdFilter.value) ||
    filterKind.value !== 'all' ||
    filterContext.value !== 'all' ||
    filterPaymentKind.value !== 'all' ||
    (rentProgressPhase.value === 'contract' &&
      (rentProgressContractPeriod.value !== 'LAST_30_DAYS' ||
        rentProgressContractStatus.value !== 'ALL')) ||
    (rentProgressPhase.value === 'documentation' &&
      rentProgressDocumentationPeriod.value !== 'LAST_30_DAYS') ||
    Boolean(filterActivityMonths.value) ||
    Boolean(filterActivityStartDate.value) ||
    Boolean(filterActivityEndDate.value)
);

const rentProgressHeaderLabel = computed(() =>
  isRentProgressPhaseEnabled.value
    ? rentProgressPhaseHeader({
        phase: rentProgressPhase.value,
        count: visibleOffers.value.length,
      })
    : ''
);

const rentProgressHeaderSupportText = computed(() =>
  isRentProgressPhaseEnabled.value
    ? rentProgressPhaseSupportText({
        phase: rentProgressPhase.value,
        period: rentProgressContractPeriod.value,
        documentationPeriod: rentProgressDocumentationPeriod.value,
      })
    : ''
);

const offerLoadMoreLabel = computed(() =>
  isRentProgressPhaseEnabled.value
    ? rentProgressPhaseLoadMoreLabel(rentProgressPhase.value)
    : 'Ver mais'
);

const showOfferLoadMore = computed(
  () =>
    !loading.value &&
    !loadError.value &&
    baseVisibleOffers.value.length > 0 &&
    !isLastOfferPage.value
);

const resetContractForm = () => {
  contractForm.value = {
    pdf_url: '',
    template_id: '',
  };
  contractCancelReason.value = '';
  contractResultMessage.value = '';
  contractActionErrorMessage.value = '';
  selectedContractProviderStatus.value = null;
};

const resetKycForm = () => {
  kycForm.value = {
    document_type: 'CNH',
    consent_accepted: false,
  };
  kycCapturePayload.value = {
    selfie_base64: '',
    document_base64: '',
    document_back_base64: '',
  };
  showKycCapture.value = false;
};

function resetClosingSurfaces() {
  referenceNow.value = new Date().toISOString();
  selectedTransaction.value = null;
  selectedDiligenceGate.value = null;
  transactionLoading.value = false;
  transactionUnavailable.value = false;
  diligenceLoading.value = false;
  diligenceUnavailable.value = false;
  diligenceReviewInFlight.value = false;
  diligenceReviewError.value = '';
  checklistActionError.value = '';
  checklistActionInFlight.value = '';
  reservationActionInFlight.value = '';
  reservationError.value = '';
  reservationUnavailable.value = false;
}

const upsertOffer = offer => {
  if (!offer?.id) return;
  const existingIndex = offers.value.findIndex(item => item.id === offer.id);
  offers.value =
    existingIndex >= 0
      ? offers.value.map(item => (item.id === offer.id ? offer : item))
      : [offer, ...offers.value];
};

const responsePayload = data => data?.payload || data;

const responseContract = data =>
  data?.payload || data?.contract || data?.property_contract || null;

const responseKycVerification = data =>
  data?.payload || data?.kyc_verification || data?.verification || null;

const responseKycVerifications = data => {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data)) return data;
  return [];
};

const replaceRouteQueryWithoutOfferTarget = () => {
  const {
    offer_id: _offerId,
    property_offer_id: _propertyOfferId,
    sales_flow_id: _salesFlowId,
    booking_reference: _bookingReference,
    token: _token,
    ...query
  } = route.query;
  isSyncingRouteState = true;
  router
    .replace({ query })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingRouteState = false;
      }, 0);
    });
};

const replaceRouteQueryWithOfferTarget = offer => {
  if (!offer?.id) return;
  isSyncingRouteState = true;
  router
    .replace({
      query: compactQuery({
        ...route.query,
        offer_id: offer.id,
      }),
    })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingRouteState = false;
      }, 0);
    });
};

syncOfferDetailsFromRoute = async () => {
  const identifier = offerDetailsRouteIdentifier(route);
  if (!identifier) return;
  if (
    showDetailsModal.value &&
    offerIdentifierMatches(selectedOffer.value, identifier)
  ) {
    return;
  }

  const existingOffer = offers.value.find(offer =>
    offerIdentifierMatches(offer, identifier)
  );
  if (existingOffer) {
    openOfferDetails(existingOffer, { syncRoute: false, source: 'deep_link' });
    return;
  }

  try {
    const { data } = await PropertyOffersAPI.show(identifier);
    const offer = data.payload || data;
    upsertOffer(offer);
    openOfferDetails(offer, { syncRoute: false, source: 'deep_link_fetch' });
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'Não foi possível abrir a proposta solicitada.')
    );
    // A dead deep link must not keep pointing at an offer we cannot open.
    replaceRouteQueryWithoutOfferTarget();
  }
};

openOfferDetails = (offer, { syncRoute = true, source = 'card' } = {}) => {
  selectedOffer.value = offer;
  resetContractForm();
  resetKycForm();
  resetClosingSurfaces();
  showContractDrawer.value = false;
  showDetailsModal.value = true;
  if (syncRoute) replaceRouteQueryWithOfferTarget(offer);
  refreshSelectedOfferDeepState({ silent: true }).catch(() => {});
  trackOfferEvent(OFFER_PAGE_TRACKING_EVENTS.OFFER_DETAILS_PAGE_VIEW, {
    offer_id: offer?.id,
    status: offer?.status,
    source,
  });
};

const closeOfferDetails = () => {
  if (
    offerAction.value ||
    contractAction.value ||
    kycAction.value ||
    checklistActionInFlight.value ||
    reservationActionInFlight.value ||
    diligenceReviewInFlight.value
  ) {
    return;
  }
  showDetailsModal.value = false;
  selectedOffer.value = null;
  selectedContractProviderStatus.value = null;
  resetClosingSurfaces();
  replaceRouteQueryWithoutOfferTarget();
};

const openContractDetails = (
  offer,
  { syncRoute = true, source = 'contract_card' } = {}
) => {
  selectedOffer.value = offer;
  resetContractForm();
  showDetailsModal.value = false;
  showContractDrawer.value = true;
  if (syncRoute) replaceRouteQueryWithOfferTarget(offer);
  refreshSelectedContractState({ silent: true }).catch(() => {});
  trackOfferEvent('offer_contract_details_opened', {
    domain: 'rent_progress',
    offer_id: offer?.id,
    property_offer_id: offer?.id,
    status: offer?.status,
    source,
  });
};

const closeContractDetails = () => {
  if (contractAction.value) return;
  showContractDrawer.value = false;
  selectedOffer.value = null;
  selectedContractProviderStatus.value = null;
  contractResultMessage.value = '';
  contractActionErrorMessage.value = '';
  replaceRouteQueryWithoutOfferTarget();
};

const findMaterializedImovel = async payload => {
  if (!payload.external_id) return null;
  const { data } = await ImovelAPI.get({
    q: payload.external_id,
    page: 1,
    per_page: 10,
  });
  const externalIds = materializedExternalIds(payload);
  return (data.payload || []).find(item =>
    externalIds.includes(String(item.external_id || ''))
  );
};

const materializeExternalImovel = async (item, mode) => {
  const payload = normalizedExternalItem(item, mode);
  const existing = await findMaterializedImovel(payload);
  if (existing) return existing;

  const { data } = await ImovelAPI.create(payload);
  return data.payload || data;
};

const openCreateModal = () => {
  createStep.value = 'property';
  catalogMode.value = 'portfolio';
  catalogQuery.value = '';
  selectedCatalogItem.value = null;
  selectedCatalogMode.value = '';
  selectedRequesterContact.value = null;
  requesterContactQuery.value = '';
  createdOffer.value = null;
  proposalForm.value = defaultProposalForm();
  showCreateModal.value = true;
  trackOfferEvent('offer_create_opened');
};

const closeCreateModal = () => {
  if (saving.value) return;
  showCreateModal.value = false;
};

const selectCatalogItem = (item, mode = catalogMode.value) => {
  selectedCatalogItem.value = item;
  selectedCatalogMode.value = mode;
  applyCatalogItemToProposal(item);
};

const selectCatalogSearchResult = async result => {
  catalogMode.value = result.source;
  if (result.source === 'portfolio') {
    selectCatalogItem(result.item, 'portfolio');
    return;
  }

  propertyMaterializing.value = true;
  try {
    const internalImovel = await materializeExternalImovel(
      result.item,
      result.source
    );
    catalogMode.value = 'portfolio';
    selectCatalogItem(internalImovel, 'portfolio');
    useAlert('Imóvel na casa selecionado para criar a proposta.');
  } catch {
    selectedCatalogItem.value = null;
    selectedCatalogMode.value = '';
    useAlert('Não foi possível preparar este imóvel para a proposta.');
  } finally {
    propertyMaterializing.value = false;
  }
};

const selectRequesterContact = contact => {
  selectedRequesterContact.value = contact;
  proposalForm.value.contact_id = contact.id;
  proposalForm.value.requester_name = contact.name || '';
  proposalForm.value.requester_email = contact.email || '';
  proposalForm.value.requester_phone = contact.phone_number || '';
};

const handleMortgageLinkClick = payload => {
  track('mortgage_link_click', {
    accountId: accountId.value,
    source: 'rent_flows_create_proposal',
    ...payload,
  });
};

const validateCreateStep = step => {
  if (step === 'property' && !selectedCatalogItem.value) {
    useAlert('Selecione um imóvel para criar a proposta.');
    return false;
  }
  if (step === 'property' && propertyMaterializing.value) {
    useAlert('Aguarde o imóvel ser preparado para criar a proposta.');
    return false;
  }

  if (
    step === 'buyer' &&
    !proposalForm.value.contact_id &&
    !proposalForm.value.requester_name.trim() &&
    !proposalForm.value.requester_email.trim() &&
    !proposalForm.value.requester_phone.trim()
  ) {
    useAlert('Informe nome, e-mail ou telefone do interessado.');
    return false;
  }

  if (step === 'values' && !proposalForm.value.offer_amount) {
    useAlert('Informe o valor da proposta.');
    return false;
  }
  if (step === 'values') {
    syncPaymentBreakdownDefaultsNow();
    if (
      isRentalProposalForm.value &&
      proposalForm.value.custom_solicitation_type &&
      !proposalForm.value.custom_solicitation_description.trim()
    ) {
      useAlert('Descreva os ajustes no imóvel solicitados.');
      return false;
    }
    if (
      isPurchaseProposalForm.value &&
      paymentNeedsFgts(proposalForm.value.payment_kind) &&
      !proposalForm.value.fgts_amount
    ) {
      useAlert('Informe o Valor de FGTS.');
      return false;
    }
    if (
      isPurchaseProposalForm.value &&
      isFinancingPayment(proposalForm.value.payment_kind) &&
      !proposalForm.value.financed_amount
    ) {
      useAlert('Informe o Valor financiado.');
      return false;
    }
    if (
      isPurchaseProposalForm.value &&
      Math.abs(paymentBreakdownDifference.value) >= 1
    ) {
      useAlert(
        `Os valores precisam somar ${formatFormBRL(proposalAmountNumber.value)}.`
      );
      return false;
    }
  }

  return true;
};

const goToCreateStep = step => {
  const nextIndex = createSteps.findIndex(item => item.value === step);
  if (nextIndex <= createStepIndex.value) {
    createStep.value = step;
    return;
  }

  const previousSteps = createSteps.slice(0, nextIndex);
  if (previousSteps.every(item => validateCreateStep(item.value))) {
    createStep.value = step;
  }
};

const nextCreateStep = () => {
  if (!validateCreateStep(createStep.value)) return;
  const nextStep = createSteps[createStepIndex.value + 1];
  if (nextStep) createStep.value = nextStep.value;
};

const previousCreateStep = () => {
  const previousStep = createSteps[createStepIndex.value - 1];
  if (previousStep) createStep.value = previousStep.value;
};

const canGoBackCreateStep = computed(() => createStepIndex.value > 0);
const isReviewStep = computed(() => createStep.value === 'review');

const openSelectedCatalogLink = () => {
  const url = selectedCatalogSummary.value?.link;
  if (!url || typeof window === 'undefined') return;

  window.open(url, '_blank', 'noopener');
};

function refreshSelectedOffer(updatedOffer) {
  if (!updatedOffer?.id) return;
  selectedOffer.value = updatedOffer;
  offers.value = offers.value.map(offer =>
    offer.id === updatedOffer.id ? updatedOffer : offer
  );
}

async function refreshSelectedOfferFromApi(id = selectedOffer.value?.id) {
  if (!id) return null;

  const { data } = await PropertyOffersAPI.show(id);
  const offer = responsePayload(data);
  refreshSelectedOffer(offer);
  return offer;
}

async function refreshSelectedOfferPropertyInfo(offer = selectedOffer.value) {
  const imovelId = offerImovelId(offer);
  if (!imovelId) return null;

  try {
    const { data } = await ImovelAPI.show(imovelId);
    const imovel = data.payload || data;
    if (!imovel) return null;
    refreshSelectedOffer({
      ...offer,
      imovel,
      imovel_titulo: imovel.titulo || imovel.title || offer.imovel_titulo,
      imovel_endereco:
        imovel.endereco ||
        imovel.address ||
        imovel.formatted_address ||
        offer.imovel_endereco,
    });
    return imovel;
  } catch (error) {
    // Property enrichment is cosmetic: keep the offer usable, but make the
    // failure observable instead of dropping it on the floor.
    trackOfferEvent('offer_property_enrichment_failed', {
      offer_id: offer?.id,
      imovel_id: imovelId,
      reason: apiErrorMessage(error, 'unknown'),
    });
    return null;
  }
}

const createProposal = async () => {
  if (!selectedCatalogItem.value) {
    useAlert('Selecione um imóvel para criar a proposta.');
    return;
  }
  if (propertyMaterializing.value) {
    useAlert('Aguarde o imóvel ser preparado para criar a proposta.');
    return;
  }

  if (!createSteps.every(item => validateCreateStep(item.value))) return;

  saving.value = true;
  trackOfferEvent('offer_create_confirm_clicked', {
    imovel_id: selectedCatalogItem.value?.id,
    business_context: proposalForm.value.business_context,
  });
  try {
    const { data } = await PropertyOffersAPI.create(createPayload());
    const offer = responsePayload(data);
    createdOffer.value = offer;
    selectedOffer.value = offer;
    useAlert('Proposta criada com sucesso.');
    trackOfferEvent('offer_created', {
      offer_id: offer?.id,
      imovel_id: offer?.imovel_id || selectedCatalogItem.value?.id,
    });
    showCreateModal.value = false;
    showCreateSuccessModal.value = true;
    resetOfferListSize();
    await fetchOffers();
    await refreshSelectedOfferPropertyInfo(offer);
  } catch (error) {
    const messages = error?.response?.data?.errors;
    useAlert(
      Array.isArray(messages) && messages.length
        ? messages.join(', ')
        : 'Não foi possível criar a proposta.'
    );
    trackOfferEvent('offer_create_error', {
      imovel_id: selectedCatalogItem.value?.id,
      reason: Array.isArray(messages) ? messages.join(', ') : 'unknown',
    });
  } finally {
    saving.value = false;
  }
};

const closeCreateSuccessModal = () => {
  showCreateSuccessModal.value = false;
};

const openCreatedOfferDetails = () => {
  if (!createdOffer.value) return;
  showCreateSuccessModal.value = false;
  openOfferDetails(createdOffer.value, { source: 'create_success' });
};

const statusLabel = status => getOfferStatusLabel(status);

const contextLabel = context => getOfferBusinessContextLabel(context);

const kindLabel = kind => getOfferKindLabel(kind);

const statusClass = status => getOfferStatusClass(status);

const paymentFilterLabel = computed(() =>
  filterPaymentKind.value === 'all'
    ? 'Todas'
    : paymentLabel(filterPaymentKind.value)
);

const offerKindFilterLabel = computed(
  () =>
    offerKindOptions.find(option => option.value === filterKind.value)?.label ||
    'Todas'
);

const businessContextOptions = [
  { value: 'all', label: 'Aluguel e compra' },
  { value: 'rental', label: 'Aluguel' },
  { value: 'purchase', label: 'Compra' },
];

const negotiationStatusOptions = [
  { value: 'all', label: 'Todas as situações' },
  { value: 'submitted', label: 'Aguardando resposta' },
  { value: 'accepted', label: 'Aceitas' },
];

const selectBusinessContext = value => {
  const normalized = businessContextOptions.some(
    option => option.value === value
  )
    ? value
    : 'all';
  if (normalized === filterContext.value) return;
  filterContext.value = normalized;
  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_QUICK_FILTERS_CLICKED,
    offerTypeFilterPayload(normalized)
  );
  applyFilters();
};

const selectNegotiationStatus = value => {
  const normalized = negotiationStatusOptions.some(
    option => option.value === value
  )
    ? value
    : 'all';
  if (normalized === negotiationStatus.value) return;
  negotiationStatus.value = normalized;
  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_QUICK_FILTERS_CLICKED,
    offerTypeFilterPayload(normalized)
  );
  applyFilters();
};

const activityFilterLabel = computed(() =>
  buildActivityFilterLabel({
    months: filterActivityMonths.value,
    startDate: filterActivityStartDate.value,
    endDate: filterActivityEndDate.value,
  })
);

const contractFilterLabel = computed(() =>
  rentProgressContractFilterLabel({
    period: rentProgressContractPeriod.value,
    status: rentProgressContractStatus.value,
  })
);

const documentationFilterLabel = computed(() =>
  rentProgressDocumentationFilterLabel({
    period: rentProgressDocumentationPeriod.value,
  })
);

const clientSearchOptions = computed(() => {
  const byKey = new Map();
  offers.value.forEach(offer => {
    const label =
      offer.contact_name ||
      offer.contact_email ||
      offer.contact_phone_number ||
      'Cliente não vinculado';
    if (!label || label === 'Cliente não vinculado') return;
    const key = String(offer.contact_id || label).toLowerCase();
    if (byKey.has(key)) return;
    byKey.set(key, {
      key,
      label,
      initials: clientInitials(label),
      phone: offer.contact_phone_number,
      email: offer.contact_email,
      contactId: offer.contact_id,
    });
  });

  return Array.from(byKey.values());
});

const selectClientSearchOption = option => {
  isSelectingClientSearchOption = true;
  searchTerm.value = option.label;
  contactIdFilter.value = option.contactId || '';
  resetOfferListSize();
  fetchOffers();
  setTimeout(() => {
    isSelectingClientSearchOption = false;
  }, 0);
};

const selectedOfferValueRows = computed(() =>
  valueRowsForOffer(selectedOffer.value)
);

const acceptValueRows = computed(() => selectedOfferValueRows.value);

const selectedOfferSolicitations = computed(() =>
  customSolicitations(selectedOffer.value)
);

const selectedOfferDecision = computed(() =>
  offerDecision(selectedOffer.value)
);

const selectedOfferCanceledReason = computed(() =>
  offerCanceledReason(selectedOffer.value)
);

const selectedOfferAcceptedSummary = computed(() =>
  offerAcceptedSummary(selectedOffer.value)
);

const selectedOfferSteps = computed(() =>
  offerProgressSteps(selectedOffer.value)
);

const selectedOfferProgressAlert = computed(() =>
  offerProgressAlert(selectedOffer.value)
);

const selectedContract = computed(() => contractForOffer(selectedOffer.value));
const selectedKycVerifications = computed(() =>
  kycVerificationsForOffer(selectedOffer.value)
);
const selectedKycVerification = computed(
  () => selectedKycVerifications.value[0] || null
);
const selectedActiveKycVerification = computed(() =>
  activeKycVerificationForOffer(selectedOffer.value)
);
// A verification that already passed must not be re-run: re-submitting would
// invalidate an approved identity check. Only a genuinely retryable outcome
// (rejected/failed/expired) reopens the submit path.
const selectedLatestKycVerification = computed(
  () => selectedKycVerifications.value[0] || null
);

const isSelectedKycRetryable = computed(() =>
  isRetryableKycVerification(selectedLatestKycVerification.value)
);

const canSubmitKycVerification = computed(
  () =>
    canCreateKycVerificationForOffer(selectedOffer.value) &&
    (!selectedLatestKycVerification.value || isSelectedKycRetryable.value)
);

const kycSubmitLabel = computed(() => {
  if (!selectedKycVerifications.value.length) return 'Enviar KYC';
  return isSelectedKycRetryable.value ? 'Refazer KYC' : 'Reenviar KYC';
});

const kycActiveAttemptMessage = computed(() => {
  if (selectedActiveKycVerification.value) {
    return 'Já existe uma tentativa KYC ativa para esta oferta. Sincronize o status antes de reenviar.';
  }
  if (selectedLatestKycVerification.value && !isSelectedKycRetryable.value) {
    return 'Esta verificação KYC já foi concluída e não precisa ser refeita.';
  }
  return '';
});

const selectedKycProviderError = computed(() =>
  kycProviderErrorMessage(selectedKycVerification.value)
);

const canGenerateContract = computed(() => {
  const offer = selectedOffer.value;
  const contract = selectedContract.value;
  return (
    offer?.status === 'accepted' &&
    !['sent', 'partially_signed', 'signed'].includes(contract?.status)
  );
});

const canSyncContract = computed(() =>
  ['sent', 'partially_signed', 'signed', 'failed'].includes(
    selectedContract.value?.status
  )
);

const canCancelContract = computed(() =>
  ['sent', 'partially_signed', 'failed'].includes(
    selectedContract.value?.status
  )
);

const contractPayload = () =>
  compactQuery({
    pdf_url: contractForm.value.pdf_url.trim(),
    template_id: contractForm.value.template_id.trim(),
  });

const applyContractResponse = data => {
  if (!selectedOffer.value) return;

  const contract = responseContract(data);
  const timeline = data?.timeline || {};
  const nextOffer = {
    ...selectedOffer.value,
  };
  if (data?.provider) {
    selectedContractProviderStatus.value = data.provider;
  }
  if (contract) {
    const contracts = Array.isArray(nextOffer.property_contracts)
      ? nextOffer.property_contracts.filter(
          item => item.id !== contract.id && item.kind !== contract.kind
        )
      : [];
    nextOffer.ccv_contract = contract;
    nextOffer.property_contracts = [contract, ...contracts];
  }
  if (Array.isArray(timeline.steps)) nextOffer.timeline = timeline.steps;
  if (timeline.current_step) nextOffer.current_step = timeline.current_step;
  if (timeline.macro_status) nextOffer.macro_status = timeline.macro_status;
  if (timeline.macro_status_label) {
    nextOffer.macro_status_label = timeline.macro_status_label;
  }
  refreshSelectedOffer(nextOffer);
};

refreshSelectedContractState = async ({ silent = false } = {}) => {
  if (!selectedOffer.value?.id) return null;

  try {
    const { data } = await PropertyOffersAPI.getContract(
      selectedOffer.value.id
    );
    applyContractResponse(data);
    return data;
  } catch (error) {
    const message = contractOperationErrorMessage(
      error,
      'Não foi possível carregar o contrato.'
    );
    contractActionErrorMessage.value = message;
    if (!silent) useAlert(message);
    return null;
  }
};

const performContractAction = async action => {
  if (!selectedOffer.value?.id || contractAction.value) return;

  const cancellationReason = normalizeContractCancelReason(
    contractCancelReason.value
  );
  if (action === 'cancel' && !cancellationReason) {
    contractActionErrorMessage.value =
      'Informe o motivo do cancelamento antes de cancelar o contrato.';
    showContractDrawer.value = true;
    return;
  }

  contractResultMessage.value = '';
  contractActionErrorMessage.value = '';
  contractAction.value = action;
  try {
    const actionMap = {
      generate: {
        request: () =>
          PropertyOffersAPI.generateContract(
            selectedOffer.value.id,
            contractPayload()
          ),
        success: 'CCV enviado para assinatura.',
      },
      sync: {
        request: () => PropertyOffersAPI.syncContract(selectedOffer.value.id),
        success: 'Status do CCV atualizado.',
      },
      cancel: {
        request: () =>
          PropertyOffersAPI.cancelContract(selectedOffer.value.id, {
            cancellation_reason: cancellationReason,
          }),
        success: 'CCV cancelado.',
      },
    };
    const actionConfig = actionMap[action];
    if (!actionConfig) return;

    const { data } = await actionConfig.request();
    applyContractResponse(data);
    await refreshSelectedOfferFromApi();
    await refreshSelectedOfferPropertyInfo();
    await fetchOffers({ syncRoute: false });
    contractResultMessage.value = actionConfig.success;
    if (action === 'cancel') contractCancelReason.value = '';
    useAlert(actionConfig.success);
  } catch (error) {
    const message = contractOperationErrorMessage(
      error,
      'Não foi possível atualizar o CCV.'
    );
    contractActionErrorMessage.value = message;
    useAlert(message);
  } finally {
    contractAction.value = '';
  }
};

const openContractSignerSignature = signer => {
  trackOfferEvent('rent_progress_contract_signature_opened', {
    domain: 'rent_progress',
    rent_progress_status: 'contract',
    property_offer_id: selectedOffer.value?.id,
    contract_id: selectedContract.value?.id,
    signer_role: signer.role,
    signer_status: signer.status,
    has_sign_url: Boolean(signer.signUrl),
  });
};

const performPublicLinkAction = async ({ action, payload }) => {
  if (!selectedOffer.value?.id || publicLinkAction.value) return;

  publicLinkAction.value = action;
  publicLinkError.value = '';
  try {
    const { data } =
      action === 'revoke'
        ? await PropertyOffersAPI.revokePublicToken(
            selectedOffer.value.id,
            payload
          )
        : await PropertyOffersAPI.reissuePublicToken(
            selectedOffer.value.id,
            payload
          );
    refreshSelectedOffer(data.payload || data);
    useAlert(
      action === 'revoke'
        ? 'Link público revogado com auditoria.'
        : 'Link público reemitido com auditoria.'
    );
  } catch (error) {
    publicLinkError.value = apiErrorMessage(
      error,
      'Não foi possível atualizar o link público.'
    );
    useAlert(publicLinkError.value);
  } finally {
    publicLinkAction.value = '';
  }
};

const revokeSelectedPublicLink = payload =>
  performPublicLinkAction({ action: 'revoke', payload });

const reissueSelectedPublicLink = payload =>
  performPublicLinkAction({ action: 'reissue', payload });

const applyKycResponse = verification => {
  if (!selectedOffer.value || !verification) return;

  const verifications = Array.isArray(selectedOffer.value.kyc_verifications)
    ? selectedOffer.value.kyc_verifications.filter(
        item => item.id !== verification.id
      )
    : [];
  refreshSelectedOffer({
    ...selectedOffer.value,
    kyc_verification: verification,
    kyc_verifications: [verification, ...verifications],
  });
};

const applyKycListResponse = data => {
  if (!selectedOffer.value) return;

  const verifications = responseKycVerifications(data);
  refreshSelectedOffer({
    ...selectedOffer.value,
    kyc_verification: verifications[0] || null,
    kyc_verifications: verifications,
  });
};

const fetchKycVerificationsForSelectedOffer = async () => {
  if (!selectedOffer.value?.id) return [];

  const { data } = await PropertyOffersAPI.kycVerifications(
    selectedOffer.value.id
  );
  applyKycListResponse(data);
  return responseKycVerifications(data);
};

// --- Closing surfaces (diligence gate, transaction checklist, reservation) ---
//
// These three panels came from the retired property_offers page. They load
// lazily when the details drawer opens, and each is feature-gated on the
// backend: a 403/404 must degrade into a calm "unavailable" state instead of an
// error banner over an otherwise healthy offer.

const fetchDiligenceGateForSelectedOffer = async () => {
  if (!selectedOffer.value?.id) return null;

  diligenceLoading.value = true;
  try {
    const { data } = await PropertyOffersAPI.kycDiligenceChecklist(
      selectedOffer.value.id
    );
    const gate = data?.gate || data?.payload || data || null;
    selectedDiligenceGate.value = gate;
    diligenceUnavailable.value = false;
    diligenceReviewError.value = '';
    return gate;
  } catch (error) {
    selectedDiligenceGate.value = null;
    diligenceUnavailable.value = isUnavailableFeatureError(error);
    diligenceReviewError.value = diligenceUnavailable.value
      ? ''
      : apiErrorMessage(
          error,
          'Não foi possível carregar a diligência da proposta.'
        );
    return null;
  } finally {
    diligenceLoading.value = false;
  }
};

const fetchTransactionForSelectedOffer = async () => {
  if (!selectedOffer.value?.id) return null;

  transactionLoading.value = true;
  try {
    const { data } = await PropertyTransactionsAPI.findByOffer(
      selectedOffer.value.id
    );
    const transaction = data ? responsePayload(data) : null;
    selectedTransaction.value = transaction;
    transactionUnavailable.value = false;
    checklistActionError.value = '';
    return transaction;
  } catch (error) {
    selectedTransaction.value = null;
    transactionUnavailable.value = isUnavailableFeatureError(error);
    checklistActionError.value = transactionUnavailable.value
      ? ''
      : apiErrorMessage(
          error,
          'Não foi possível carregar o checklist de fechamento.'
        );
    return null;
  } finally {
    transactionLoading.value = false;
  }
};

const handleDiligenceReview = async payload => {
  if (!selectedOffer.value?.id || diligenceReviewInFlight.value) return;

  diligenceReviewInFlight.value = true;
  diligenceReviewError.value = '';
  try {
    const { data } = await PropertyOffersAPI.reviewKycDiligence(
      selectedOffer.value.id,
      payload
    );
    selectedDiligenceGate.value =
      data?.gate || data?.payload || selectedDiligenceGate.value;
    // The gate decides whether the contract can be generated, so the offer has
    // to be re-read before the operator touches the contract panel.
    await refreshSelectedOfferFromApi();
    await fetchDiligenceGateForSelectedOffer();
    useAlert('Decisão de diligência registrada.');
    trackOfferEvent('offer_diligence_reviewed', {
      offer_id: selectedOffer.value?.id,
      item_key: payload?.item_key,
      offer_diligence_decision: payload?.decision,
    });
  } catch (error) {
    diligenceReviewError.value = apiErrorMessage(
      error,
      'Não foi possível registrar a decisão de diligência.'
    );
    useAlert(diligenceReviewError.value);
  } finally {
    diligenceReviewInFlight.value = false;
  }
};

const checklistCommands = {
  complete: (txId, itemId, payload) =>
    PropertyTransactionsAPI.completeItem(txId, itemId, {
      refs: payload?.refs || [],
    }),
  reopen: (txId, itemId, payload) =>
    PropertyTransactionsAPI.reopenItem(txId, itemId, {
      reason: payload?.reason,
    }),
  waive: (txId, itemId, payload) =>
    PropertyTransactionsAPI.waiveItem(txId, itemId, {
      reason: payload?.reason,
    }),
  unblock: (txId, itemId) => PropertyTransactionsAPI.unblockItem(txId, itemId),
  add_evidence: (txId, itemId, payload) =>
    PropertyTransactionsAPI.addEvidence(txId, itemId, payload?.refs || []),
};

const handleChecklistItemAction = async ({ item, action, payload } = {}) => {
  const transactionId = selectedTransaction.value?.id;
  // The presenter exposes `sourceId`; the panel keys its per-item busy state on
  // the same value, so both sides must agree or every button stays enabled.
  const itemId = item?.sourceId || item?.id;
  const command = checklistCommands[action];
  if (!transactionId || !itemId || !command || checklistActionInFlight.value) {
    return;
  }

  checklistActionInFlight.value = `${action}:${itemId}`;
  checklistActionError.value = '';
  try {
    const { data } = await command(transactionId, itemId, payload);
    selectedTransaction.value = responsePayload(data);
    useAlert('Checklist de fechamento atualizado.');
    trackOfferEvent('offer_transaction_item_action', {
      offer_id: selectedOffer.value?.id,
      transaction_id: transactionId,
      item_key: item?.key || itemId,
      offer_checklist_action: action,
    });
  } catch (error) {
    checklistActionError.value = apiErrorMessage(
      error,
      'Não foi possível atualizar o item do checklist.'
    );
    useAlert(checklistActionError.value);
  } finally {
    checklistActionInFlight.value = '';
  }
};

// The diligence gate and the closing checklist only exist once an offer reaches
// the closing phase. Hiding them (instead of rendering an empty frame) keeps the
// drawer free of dead surfaces for offers still under negotiation, and a
// feature-gated 403 keeps them hidden too.
const showDiligencePanel = computed(
  () =>
    !diligenceUnavailable.value &&
    Boolean(selectedDiligenceGate.value || diligenceLoading.value)
);

const showTransactionChecklist = computed(
  () =>
    !transactionUnavailable.value &&
    Boolean(
      selectedTransaction.value ||
        transactionLoading.value ||
        selectedOffer.value?.status === 'accepted'
    )
);

const selectedReservationHold = computed(
  () =>
    selectedOffer.value?.reservation_hold ||
    selectedOffer.value?.reservation ||
    null
);

const performReservationAction = async (action, payload = {}) => {
  const hold = selectedReservationHold.value;
  if (!hold?.id || reservationActionInFlight.value) return;

  reservationActionInFlight.value = action;
  reservationError.value = '';
  try {
    if (action === 'refund') {
      if (!payload.intentId) return;
      await ReservationHoldsAPI.refundPaymentIntent(payload.intentId, {
        amount: payload.amount,
        // Deterministic key: retrying the same refund must never double-refund.
        idempotency_key: `offer-${selectedOffer.value?.id}-reservation-refund-${payload.intentId}`,
      });
    } else if (action === 'confirm') {
      await ReservationHoldsAPI.confirm(hold.id);
    } else if (action === 'release') {
      await ReservationHoldsAPI.release(hold.id, {
        reason: 'released_from_offer_reservation_panel',
      });
    } else if (action === 'cancel') {
      await ReservationHoldsAPI.cancel(hold.id, {
        reason: 'canceled_from_offer_reservation_panel',
      });
    } else {
      return;
    }

    await refreshSelectedOfferFromApi();
    useAlert('Reserva atualizada.');
    trackOfferEvent('offer_reservation_action', {
      offer_id: selectedOffer.value?.id,
      reservation_hold_id: hold.id,
      offer_reservation_action: action,
    });
  } catch (error) {
    reservationUnavailable.value = isUnavailableFeatureError(error);
    reservationError.value = apiErrorMessage(
      error,
      'Não foi possível atualizar a reserva.'
    );
    if (!reservationUnavailable.value) useAlert(reservationError.value);
  } finally {
    reservationActionInFlight.value = '';
  }
};

const confirmSelectedReservation = () => performReservationAction('confirm');
const releaseSelectedReservation = () => performReservationAction('release');
const cancelSelectedReservation = () => performReservationAction('cancel');
const refundSelectedReservation = payload =>
  performReservationAction('refund', payload || {});

refreshSelectedOfferDeepState = async ({ silent = false } = {}) => {
  if (!selectedOffer.value?.id) return;

  const offerId = selectedOffer.value.id;
  operationalContextLoading.value = true;
  operationalContextError.value = '';

  const operationalContextRequest = PropertyOffersAPI.show(offerId)
    .then(({ data }) => {
      if (selectedOffer.value?.id !== offerId) return;
      refreshSelectedOffer(responsePayload(data));
    })
    .catch(error => {
      if (selectedOffer.value?.id === offerId) {
        operationalContextError.value = apiErrorMessage(
          error,
          'Não foi possível carregar as tarefas e automações.'
        );
      }
    });

  try {
    const [{ data: contractData }] = await Promise.all([
      PropertyOffersAPI.getContract(selectedOffer.value.id),
      fetchKycVerificationsForSelectedOffer(),
      // The gate and the checklist own their own error state; they must never
      // fail the contract/KYC sync the drawer depends on.
      fetchDiligenceGateForSelectedOffer(),
      fetchTransactionForSelectedOffer(),
    ]);
    applyContractResponse(contractData);
  } catch (error) {
    if (!silent) {
      useAlert(
        apiErrorMessage(error, 'Não foi possível sincronizar contrato e KYC.')
      );
    }
  } finally {
    await operationalContextRequest;
    if (selectedOffer.value?.id === offerId) {
      operationalContextLoading.value = false;
    }
  }
};

const refreshKycVerifications = async () => {
  if (!selectedOffer.value?.id || kycAction.value) return;

  kycAction.value = 'sync';
  try {
    await refreshSelectedOfferFromApi();
    await fetchKycVerificationsForSelectedOffer();
    await fetchOffers({ syncRoute: false });
    useAlert('Status do KYC atualizado.');
  } catch (error) {
    useAlert(apiErrorMessage(error, 'Não foi possível atualizar o KYC.'));
  } finally {
    kycAction.value = '';
  }
};

const handleKycCaptured = payload => {
  if (payload.target === 'selfie') {
    kycCapturePayload.value.selfie_base64 = payload.base64;
    return;
  }

  kycCapturePayload.value.document_base64 = payload.base64;
  kycCapturePayload.value.document_back_base64 = payload.base64Back || '';
};

const handleKycCaptureFailure = payload => {
  useAlert(payload?.message || 'Não foi possível capturar a biometria.');
};

const handleKycUnsupported = () => {
  useAlert('Este navegador não suporta a captura biométrica.');
};

const submitKycVerification = async () => {
  if (!selectedOffer.value?.id || kycAction.value) return;
  if (!canSubmitKycVerification.value) {
    useAlert(kycActiveAttemptMessage.value);
    return;
  }
  if (!kycForm.value.consent_accepted) {
    useAlert('Aceite o consentimento LGPD para enviar o KYC.');
    return;
  }
  if (!kycCapturePayload.value.selfie_base64) {
    useAlert('Capture a selfie antes de enviar o KYC.');
    return;
  }
  if (!kycCapturePayload.value.document_base64) {
    useAlert('Capture o documento antes de enviar o KYC.');
    return;
  }

  kycAction.value = 'submit';
  try {
    const { data } = await PropertyOffersAPI.createKycVerification(
      selectedOffer.value.id,
      {
        selfie_base64: kycCapturePayload.value.selfie_base64,
        document_base64: kycCapturePayload.value.document_base64,
        document_back_base64: kycCapturePayload.value.document_back_base64,
        document_type: kycForm.value.document_type,
        consent_accepted: kycForm.value.consent_accepted,
        metadata: {
          capture: 'acessobio-webframe',
          has_document_back: Boolean(
            kycCapturePayload.value.document_back_base64
          ),
        },
      }
    );
    applyKycResponse(responseKycVerification(data));
    await refreshSelectedOfferFromApi();
    await fetchKycVerificationsForSelectedOffer();
    await fetchOffers({ syncRoute: false });
    useAlert(
      data?.active_verification_reused
        ? kycActiveAttemptMessage.value
        : 'KYC enviado para validação.'
    );
  } catch (error) {
    useAlert(apiErrorMessage(error, 'Não foi possível enviar o KYC.'));
  } finally {
    kycAction.value = '';
  }
};

const rentProgressActionsForOffer = offer =>
  isRentProgressPhaseEnabled.value
    ? rentProgressCardActions(offer, rentProgressPhase.value)
    : [];

const rentProgressSummariesForOffer = offer =>
  isRentProgressPhaseEnabled.value &&
  rentProgressPhase.value === RENT_PROGRESS_PHASES.NEGOTIATION
    ? rentProgressNegotiationSummaries(offer)
    : [];

const trackRentProgressCardAction = (offer, action) => {
  const rentProgressStatus = rentProgressPhase.value;
  const payload = {
    domain: 'rent_progress',
    offer_id: offer?.id,
    rent_progress_status: rentProgressStatus,
    tab_name: rentProgressStatus,
    cta: action,
  };

  if (action === 'chat_client' || action === 'owner_contact') {
    trackOfferEvent('chatcrpp_access_chat_clicked', payload);
    return;
  }

  trackOfferEvent('rent_progress_status_card_opened', payload);
};

// Registers the "proposal link sent" event and hands the operator the deep link
// the client should receive. The backend answers `already_sent` so a resend is
// labelled honestly instead of pretending it is the first delivery.
const sendOfferLinkToClient = async (offer = selectedOffer.value) => {
  const imovelId = offerImovelId(offer);
  const contactId = offerContactId(offer);
  if (!imovelId || !contactId) {
    useAlert('Vincule imóvel e cliente à proposta antes de enviar o link.');
    return;
  }
  if (offerLinkAction.value) return;

  offerLinkAction.value = 'send';
  try {
    const { data } = await PropertyOffersAPI.sendOfferLink({
      payload: { imovel_id: imovelId, contact_id: contactId },
    });
    const deepLink = data?.deep_link || '';
    if (deepLink && typeof window !== 'undefined') {
      window.open(deepLink, '_blank', 'noopener');
    }
    useAlert(
      data?.already_sent
        ? 'Reenvio do link da proposta registrado.'
        : 'Envio do link da proposta registrado.'
    );
    trackOfferEvent('offer_link_sent', {
      offer_id: offer?.id,
      imovel_id: imovelId,
      contact_id: contactId,
      already_sent: Boolean(data?.already_sent),
      offer_link_event_id: data?.event_id,
    });
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'Não foi possível registrar o envio da proposta.')
    );
  } finally {
    offerLinkAction.value = '';
  }
};

const openClientProfile = offer => {
  const contactId = offerContactId(offer);
  if (!contactId) {
    useAlert('Este cliente ainda não tem perfil vinculado.');
    return;
  }

  router.push({
    name: 'contacts_edit',
    params: { accountId: accountId.value, contactId },
  });
};

const openClientChat = offer => {
  const conversationId = offerConversationId(offer);
  if (!conversationId) {
    useAlert('Este cliente ainda não tem conversa vinculada.');
    return;
  }

  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_DETAILS_CHAT_BUTTON_CLICKED,
    offerDetailsChatPayload({
      channel: 'chat',
      offerId: offer?.id,
      conversationId,
    })
  );
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversationId },
  });
};

const handleOfferStatusListErrorRefetch = () => {
  trackOfferEvent(
    OFFER_PAGE_TRACKING_EVENTS.OFFER_STATUS_LIST_ERROR_REFRESH_BUTTON_CLICKED,
    offerPageTrackingEnvelope()
  );
};

const openOwnerContact = offer => {
  const href = ownerWhatsappHrefForOffer(offer);
  if (!href || typeof window === 'undefined') {
    useAlert('Este proprietário ainda não tem telefone vinculado.');
    return;
  }

  window.open(href, '_blank', 'noopener');
};

const handleOfferCardOpen = offer => {
  if (
    isRentProgressPhaseEnabled.value &&
    rentProgressPhase.value === RENT_PROGRESS_PHASES.CONTRACT
  ) {
    openContractDetails(offer, {
      source: 'rent_progress_contract_card',
    });
    return;
  }

  openOfferDetails(offer);
};

const handleRentProgressCardAction = ({ action, offer }) => {
  trackRentProgressCardAction(offer, action);

  if (action === 'chat_client') {
    openClientChat(offer);
    return;
  }

  if (action === 'owner_contact') {
    openOwnerContact(offer);
    return;
  }

  if (action === 'contract') {
    openContractDetails(offer, {
      source: `rent_progress_${action}`,
    });
    return;
  }

  openOfferDetails(offer, {
    source: `rent_progress_${action}`,
  });
};

// Scrolls the details drawer to a panel the progress trail points at. The
// purchase trail routes to surfaces that live further down the same drawer, so
// navigating means bringing the panel into view — not opening a new screen.
const focusDrawerPanel = (selectors, fallbackMessage = '') => {
  if (typeof document === 'undefined') return false;
  const candidates = Array.isArray(selectors) ? selectors : [selectors];
  const target = candidates
    .map(selector => document.querySelector(selector))
    .find(Boolean);
  if (!target) {
    if (fallbackMessage) useAlert(fallbackMessage);
    return false;
  }
  if (target.scrollIntoView) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  return true;
};

const handleProgressStepAction = async (step, action) => {
  const offer = selectedOffer.value;
  if (!offer || !action?.key) return;
  // A blocked CTA must explain itself, never silently no-op.
  if (action.disabled) {
    useAlert(action.hint || action.blockReason || 'Ação indisponível agora.');
    return;
  }

  trackOfferEvent(action.event || 'rent_progress_step_action_clicked', {
    domain: 'rent_progress',
    property_offer_id: offer.id,
    offer_id: offer.id,
    step_key: step?.key || step?.label,
    cta: action.key,
  });

  if (action.key === 'capture_kyc') {
    showKycCapture.value = true;
    return;
  }

  if (action.key === 'submit_kyc') {
    await submitKycVerification();
    return;
  }

  if (action.key === 'chat_client') {
    openClientChat(offer);
    return;
  }

  if (action.key === 'owner_contact') {
    openOwnerContact(offer);
    return;
  }

  if (action.key === 'generate_contract') {
    await performContractAction('generate');
    return;
  }

  if (action.key === 'contract') {
    openContractDetails(offer, { source: 'progress_step_contract' });
    return;
  }

  if (action.key === 'kyc') {
    // Make sure the gate is fresh before pointing the operator at it.
    await fetchDiligenceGateForSelectedOffer();
    focusDrawerPanel(
      ['[data-offer-panel="diligence"]', '[data-offer-panel="kyc"]'],
      'A diligência ainda não está disponível para esta proposta.'
    );
    return;
  }

  if (action.key === 'transaction_checklist') {
    await fetchTransactionForSelectedOffer();
    focusDrawerPanel(
      '[data-offer-panel="transaction-checklist"]',
      'O checklist de fechamento ainda não foi aberto para esta proposta.'
    );
    return;
  }

  if (action.key === 'reservation') {
    focusDrawerPanel(
      '[data-offer-panel="reservation"]',
      'Esta proposta não tem reserva vinculada.'
    );
  }
};

const decisionStateForOffer = offer => offerDecisionState(offer);
const canAcceptOffer = offer => decisionStateForOffer(offer).canAccept;
const canDeclineOffer = offer => decisionStateForOffer(offer).canDecline;
const canWithdrawOffer = offer => decisionStateForOffer(offer).canWithdraw;
const canEditOffer = offer => {
  const state = decisionStateForOffer(offer);
  return (
    Boolean(offer?.id) && !state.accepted && !state.terminal && !state.final
  );
};
// The backend only accepts mutations while the offer is `submitted` and the
// current ledger revision is present/pending. The shared action matrix keeps
// accepted, canceled, expired and permission-denied statuses from rendering
// dead command paths.
const counterExpectedRevisionId = offer => offer?.current_revision?.id ?? null;
const canCounterOffer = offer => decisionStateForOffer(offer).canCounter;

const selectedOfferDecisionBlockedReason = computed(() =>
  selectedOffer.value
    ? decisionStateForOffer(selectedOffer.value).blockedReason
    : ''
);

const selectedDeclineReasonLabel = computed(
  () =>
    declineReasonOptions.find(
      option => option.value === declineForm.value.reason
    )?.label || 'Outro motivo'
);

const declineSubmitLabel = computed(() =>
  declineForm.value.disable_listing
    ? 'Recusar proposta e desativar anúncio'
    : 'Recusar proposta'
);

const declineReasonOptionsForSelectedOffer = computed(() => {
  if (!isRentalOffer(selectedOffer.value)) return declineReasonOptions;
  return declineReasonOptions.filter(option =>
    rentalDeclineReasons.includes(option.value)
  );
});

const selectedOfferFirstName = computed(() => {
  const buyer = buyerLabel(selectedOffer.value).trim();
  return buyer.split(/\s+/)[0] || 'Interessado';
});

const acceptModalTitle = computed(() =>
  isRentalOffer(selectedOffer.value)
    ? `${selectedOfferFirstName.value} aceitou as condições do anúncio`
    : 'Aceitar proposta'
);

const acceptedFeedbackDescription = computed(() =>
  isRentalOffer(selectedOffer.value)
    ? 'A documentação do locatário e o contrato de locação seguem pela área de propostas aceitas.'
    : 'A diligência, o checklist de fechamento e o contrato de compra e venda seguem pela área de propostas aceitas.'
);

const declinedFeedbackDescription = computed(() =>
  selectedOfferDecision.value?.disable_listing
    ? 'Essa proposta foi recusada, o anúncio foi desativado e os próximos compromissos pendentes do imóvel foram cancelados.'
    : 'Esta proposta foi recusada. Para acompanhar outras informações acesse a lista de propostas Canceladas'
);

const resetDeclineForm = () => {
  declineForm.value = {
    reason:
      declineReasonOptionsForSelectedOffer.value[0]?.value ||
      'conditions_not_agreed',
    disable_listing: false,
    notes: '',
  };
};

const openAcceptModal = () => {
  showAcceptModal.value = true;
};

const closeAcceptModal = () => {
  if (offerAction.value) return;
  showAcceptModal.value = false;
};

const closeAcceptedFeedbackModal = () => {
  showAcceptedFeedbackModal.value = false;
};

const closeDeclinedFeedbackModal = () => {
  showDeclinedFeedbackModal.value = false;
};

const showAcceptedOffers = () => {
  showAcceptedFeedbackModal.value = false;
  showDetailsModal.value = false;
  offerBucket.value = 'finished';
  negotiationStatus.value = 'all';
  resetOfferListSize();
  fetchOffers();
};

const showCanceledOffers = () => {
  showDeclinedFeedbackModal.value = false;
  showDetailsModal.value = false;
  offerBucket.value = 'canceled';
  negotiationStatus.value = 'all';
  resetOfferListSize();
  fetchOffers();
};

const openDeclineModal = () => {
  resetDeclineForm();
  showDeclineModal.value = true;
};

const closeDeclineModal = () => {
  if (offerAction.value) return;
  showDeclineModal.value = false;
};

const declineNotes = computed(() => {
  const parts = [`Motivo: ${selectedDeclineReasonLabel.value}`];
  if (declineForm.value.disable_listing) {
    parts.push('O anúncio será desativado.');
  }
  if (declineForm.value.notes.trim()) {
    parts.push(declineForm.value.notes.trim());
  }
  return parts.join(' ');
});

// Re-reads the offer straight from the API after an optimistic-lock conflict so
// the drawer stops showing the revision the operator already lost.
const reloadSelectedOfferAfterConflict = async ({
  successMessage = 'Esta proposta mudou. Carregamos a versão mais recente.',
} = {}) => {
  if (!selectedOffer.value?.id) return false;
  try {
    const { data } = await PropertyOffersAPI.show(selectedOffer.value.id);
    refreshSelectedOffer(responsePayload(data));
    counterConflict.value = false;
    useAlert(successMessage);
    return true;
  } catch (error) {
    useAlert(apiErrorMessage(error, 'Não foi possível recarregar a proposta.'));
    return false;
  }
};

const actionPayload = (action, notes) => ({
  notes,
  reason: action === 'decline' ? declineForm.value.reason : undefined,
  reason_label:
    action === 'decline' ? selectedDeclineReasonLabel.value : undefined,
  disable_listing:
    action === 'decline' ? declineForm.value.disable_listing : undefined,
  price_summary: decisionPriceSummary(selectedOffer.value),
});

const performOfferAction = async (action, notesOverride = '') => {
  if (!selectedOffer.value?.id || offerAction.value) return;

  offerAction.value = action;
  try {
    const actionMap = {
      accept: {
        request: () =>
          PropertyOffersAPI.accept(
            selectedOffer.value.id,
            actionPayload('accept', 'Proposta aceita pela Imudei.')
          ),
        success: 'Proposta aceita',
      },
      decline: {
        request: () =>
          PropertyOffersAPI.decline(
            selectedOffer.value.id,
            actionPayload(
              'decline',
              notesOverride || 'Proprietário recusou a proposta pela Imudei.'
            )
          ),
        success: 'Proposta recusada',
      },
      withdraw: {
        request: () =>
          PropertyOffersAPI.withdraw(
            selectedOffer.value.id,
            actionPayload(
              'withdraw',
              'Interessado desistiu da proposta pela Imudei.'
            )
          ),
        success: 'Proposta retirada',
      },
    };
    const actionConfig = actionMap[action];
    if (!actionConfig) return;
    const { data } = await actionConfig.request();
    refreshSelectedOffer(data);
    await refreshSelectedOfferPropertyInfo(data);
    await fetchOffers();
    if (action === 'accept') showAcceptModal.value = false;
    if (action === 'accept') showAcceptedFeedbackModal.value = true;
    if (action === 'decline') showDeclineModal.value = false;
    if (action === 'decline') showDeclinedFeedbackModal.value = true;
    useAlert(actionConfig.success);
  } catch (error) {
    // A decision can lose a race with another operator: the backend answers 409
    // and the offer in hand is stale. Reload it instead of leaving the operator
    // guessing why the button did nothing.
    if (isConflictError(error)) {
      await reloadSelectedOfferAfterConflict();
      trackOfferEvent('offer_decision_conflict', {
        offer_id: selectedOffer.value?.id,
        offer_decision: action,
      });
      return;
    }
    useAlert(apiErrorMessage(error, 'Não foi possível atualizar a proposta.'));
  } finally {
    offerAction.value = '';
  }
};

const openCounterModal = () => {
  if (!canCounterOffer(selectedOffer.value)) return;
  counterConflict.value = false;
  showCounterModal.value = true;
  trackOfferEvent('offer_counter_opened', {
    offer_id: selectedOffer.value?.id,
    status: selectedOffer.value?.status,
  });
};

const openEditModal = () => {
  if (!canEditOffer(selectedOffer.value) || offerAction.value) return;

  editError.value = '';
  showEditModal.value = true;
};

const closeEditModal = () => {
  if (offerAction.value === 'edit') return;

  showEditModal.value = false;
  editError.value = '';
};

const updateSelectedOffer = async payload => {
  if (!selectedOffer.value?.id || offerAction.value) return;

  offerAction.value = 'edit';
  editError.value = '';
  try {
    const { data } = await PropertyOffersAPI.update(
      selectedOffer.value.id,
      payload
    );
    const offer = responsePayload(data);
    refreshSelectedOffer(offer);
    await fetchOffers({ syncRoute: false });
    showEditModal.value = false;
    useAlert('Proposta atualizada com sucesso.');
  } catch (error) {
    editError.value = apiErrorMessage(
      error,
      'Não foi possível atualizar a proposta.'
    );
  } finally {
    offerAction.value = '';
  }
};

const automationEventForOffer = offer => {
  if (!offer) return 'offer_created';

  const eventByStatus = {
    accepted: 'offer_accepted',
    declined: 'offer_declined',
    withdrawn: 'offer_withdrawn',
  };

  return eventByStatus[offer.status] || 'offer_submitted';
};

const createAutomationForOffer = offer => {
  if (!isAdmin.value) return;

  const eventName = automationEventForOffer(offer);
  trackOfferEvent('offer_automation_rule_started', {
    offer_id: offer?.id,
    automation_event_name: eventName,
  });
  router.push({
    name: 'automation_list',
    params: { accountId: accountId.value },
    query: {
      create: '1',
      automation_event: eventName,
    },
  });
};

const closeCounterModal = () => {
  if (offerAction.value === 'counter') return;
  showCounterModal.value = false;
  counterConflict.value = false;
};

const reloadCounterOffer = async () => {
  await reloadSelectedOfferAfterConflict({
    successMessage: 'Proposta recarregada com a revisão mais recente.',
  });
};

const performCounterOffer = async payload => {
  if (!selectedOffer.value?.id || offerAction.value) return;

  offerAction.value = 'counter';
  counterConflict.value = false;
  try {
    const { data } = await PropertyOffersAPI.counter(selectedOffer.value.id, {
      property_offer_revision: payload.property_offer_revision,
      expected_revision_id: counterExpectedRevisionId(selectedOffer.value),
      notes: payload.notes,
    });
    const offer = data.payload || data;
    refreshSelectedOffer(offer);
    await refreshSelectedOfferPropertyInfo(offer);
    await fetchOffers();
    showCounterModal.value = false;
    useAlert('Contraproposta registrada.');
    trackOfferEvent('offer_counter_submitted', {
      offer_id: offer?.id,
      status: offer?.status,
    });
  } catch (error) {
    // Optimistic-lock guard: the backend answers 409 when expected_revision_id
    // no longer matches the current revision. Surface the stale state and let
    // the operator reload — never silently overwrite the newer revision.
    if (isConflictError(error)) {
      counterConflict.value = true;
      trackOfferEvent('offer_counter_conflict', {
        offer_id: selectedOffer.value?.id,
      });
      return;
    }
    useAlert(
      apiErrorMessage(error, 'Não foi possível registrar a contraproposta.')
    );
  } finally {
    offerAction.value = '';
  }
};

const offerEmptyState = computed(() => {
  // The server already narrows by phase/contract filters, but the page refines
  // the page it received. When that refinement empties a non-empty page, say so
  // and point at the remaining pages instead of implying there is nothing left.
  if (
    !loading.value &&
    !loadError.value &&
    baseVisibleOffers.value.length > 0 &&
    visibleOffers.value.length === 0
  ) {
    return {
      title: 'Nenhuma proposta desta fase nas carregadas',
      description: isLastOfferPage.value
        ? 'Todas as propostas deste filtro já foram carregadas.'
        : `Nenhuma das ${baseVisibleOffers.value.length} propostas carregadas está nesta fase. Carregue mais para continuar procurando.`,
      buttonLabel: isLastOfferPage.value ? '' : 'Ver mais',
    };
  }
  if (isRentProgressPhaseEnabled.value) {
    return rentProgressPhaseEmptyState({
      phase: rentProgressPhase.value,
      hasFilters: hasActiveOfferFilters.value,
      isError: loadError.value,
    });
  }
  if (loadError.value) {
    return {
      title: 'Não foi possível carregar as propostas',
      description:
        loadErrorMessage.value ||
        'Atualize a página para carregar as informações novamente',
      buttonLabel: 'Atualizar página',
    };
  }
  if (hasActiveOfferFilters.value) {
    return {
      title: 'Nenhuma proposta com esses filtros',
      description: 'Ajuste ou limpe os filtros para ver mais propostas.',
    };
  }
  if (offerBucket.value === 'finished') {
    return {
      title: 'Nenhuma proposta finalizada',
      description: '',
    };
  }
  if (offerBucket.value === 'canceled') {
    return {
      title: 'Nenhuma proposta cancelada',
      description: '',
    };
  }
  return {
    title: 'Nenhuma proposta em andamento',
    description: '',
  };
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-white font-redHatText md:overflow-hidden"
  >
    <LaisHeader :title="pageTitle" :helper="pageHelper">
      <template #actions>
        <LaisPrimaryButton
          v-if="isAdmin"
          icon="i-lucide-workflow"
          size="sm"
          variant="ghost"
          @click="createAutomationForOffer()"
        >
          Automatizar propostas
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-plus"
          size="sm"
          @click="openCreateModal"
        >
          Nova proposta
        </LaisPrimaryButton>
        <button
          type="button"
          class="grid size-10 place-items-center rounded-full text-lais-primary transition-colors hover:bg-lais-primary-50"
          aria-label="atualizar propostas"
          title="atualizar propostas"
          @click="refreshOffers"
        >
          <span class="i-lucide-refresh-cw size-5" aria-hidden="true" />
        </button>
      </template>
    </LaisHeader>

    <OfferBucketTabs
      :offer-buckets="offerBuckets"
      :offer-bucket="offerBucket"
      @select="selectOfferBucket"
    />

    <RentProgressPhaseTabs
      v-if="offerBucket === 'in_negotiation'"
      :rent-progress-phases="rentProgressPhases"
      :rent-progress-phase="rentProgressPhase"
      @select="selectRentProgressPhase"
    />

    <OfferListFilters
      v-model:search-term="searchTerm"
      :client-search-options="clientSearchOptions"
      :offer-kind-filter-label="offerKindFilterLabel"
      :payment-filter-label="paymentFilterLabel"
      :activity-filter-label="activityFilterLabel"
      :contract-filter-label="contractFilterLabel"
      :documentation-filter-label="documentationFilterLabel"
      :rent-progress-phase="rentProgressPhase"
      :property-id-filter="propertyIdFilter"
      :business-context="filterContext"
      :business-context-options="businessContextOptions"
      :negotiation-status="negotiationStatus"
      :negotiation-status-options="negotiationStatusOptions"
      :show-negotiation-status="isRentProgressPhaseEnabled"
      @open-offer-type-drawer="openOfferTypeDrawer"
      @select-business-context="selectBusinessContext"
      @select-negotiation-status="selectNegotiationStatus"
      @open-payment-kind-drawer="openPaymentKindDrawer"
      @open-activity-drawer="openActivityDrawer"
      @open-contract-filters-drawer="openContractFiltersDrawer"
      @open-documentation-filters-drawer="openDocumentationFiltersDrawer"
      @select-client-search-option="selectClientSearchOption"
      @apply-filters="applyFilters"
      @clear-property-filter="clearPropertyFilter"
    />

    <OfferCardsHeader
      v-model="sortBy"
      :count="displayCount"
      :count-label="rentProgressHeaderLabel"
      :options="offerSortOptions"
      :status-bucket="offerBucket"
      :support-text="rentProgressHeaderSupportText"
      @change="handleOfferSortChange"
    />

    <div
      class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-6 sm:pb-4 lg:px-10 max-md:flex-none max-md:overflow-visible"
    >
      <OfferListSkeleton
        v-if="loading"
        :offer-skeleton-cards="offerSkeletonCards"
      />
      <OfferListEmptyState
        v-else-if="hasNoVisibleOffers"
        :offer-empty-state="offerEmptyState"
        :load-error="loadError"
        :can-load-more="canLoadMoreFromEmptyState"
        @refresh="refreshOffers"
        @load-more="loadMoreOffers"
      />
      <div v-else class="grid gap-3">
        <OfferCard
          v-for="offer in visibleOffers"
          :key="offer.id"
          :offer="offer"
          :buyer-label="buyerLabel(offer)"
          :status-label="statusLabel(offer.status)"
          :status-detail="offerStatusDetail(offer)"
          :reference="offerReference(offer)"
          :formatted-amount="formatBRL(offer.offer_amount)"
          :rent-progress-actions="rentProgressActionsForOffer(offer)"
          :rent-progress-summaries="rentProgressSummariesForOffer(offer)"
          @open="handleOfferCardOpen"
          @rent-progress-action="handleRentProgressCardAction"
        />
      </div>
    </div>

    <div
      v-if="showOfferLoadMore"
      class="border-t border-lais-border bg-white px-4 py-3 max-sm:pl-20 sm:px-6 lg:px-10"
    >
      <button
        type="button"
        class="h-11 w-full rounded-lais-sm text-[14px] font-semibold text-lais-primary transition-colors hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-45"
        :disabled="isLastOfferPage"
        @click="loadMoreOffers"
      >
        {{ offerLoadMoreLabel || 'Ver mais' }}
      </button>
    </div>

    <OfferFilterDrawers
      v-model:activity-draft="activityDraft"
      v-model:contract-period-draft="rentProgressContractPeriodDraft"
      v-model:contract-status-draft="rentProgressContractStatusDraft"
      v-model:documentation-period-draft="rentProgressDocumentationPeriodDraft"
      v-model:offer-kind-draft="offerKindDraft"
      v-model:payment-kind-draft="paymentKindDraft"
      :activity-filter-error="activityFilterError"
      :contract-period-options="rentProgressContractPeriodOptions"
      :contract-status-options="rentProgressContractStatusOptions"
      :documentation-period-options="rentProgressDocumentationPeriodOptions"
      :offer-kind-options="offerKindOptions"
      :payment-options="paymentOptions"
      :show-activity="showActivityDrawer"
      :show-contract-filters="showContractFiltersDrawer"
      :show-documentation-filters="showDocumentationFiltersDrawer"
      :show-offer-type="showOfferTypeDrawer"
      :show-payment-kind="showPaymentKindDrawer"
      @apply-activity="applyActivityDrawer"
      @apply-contract-filters="applyContractFiltersDrawer"
      @apply-documentation-filters="applyDocumentationFiltersDrawer"
      @apply-offer-type="applyOfferTypeDrawer"
      @apply-payment-kind="applyPaymentKindDrawer"
      @clear-activity="clearActivityDrawer"
      @clear-contract-filters="clearContractFiltersDrawer"
      @clear-documentation-filters="clearDocumentationFiltersDrawer"
      @clear-offer-type="clearOfferTypeDrawer"
      @clear-payment-kind="clearPaymentKindDrawer"
      @close-activity="showActivityDrawer = false"
      @close-contract-filters="showContractFiltersDrawer = false"
      @close-documentation-filters="showDocumentationFiltersDrawer = false"
      @close-offer-type="showOfferTypeDrawer = false"
      @close-payment-kind="showPaymentKindDrawer = false"
      @select-activity-custom-range="selectActivityCustomRange"
      @select-activity-preset="selectActivityPreset"
    />

    <OfferContractDrawer
      :show="showContractDrawer"
      :offer="selectedOffer"
      :contract="selectedContract"
      :provider-status="selectedContractProviderStatus"
      :pdf-url="contractForm.pdf_url"
      :template-id="contractForm.template_id"
      :cancel-reason="contractCancelReason"
      :contract-action="contractAction"
      :result-message="contractResultMessage"
      :error-message="contractActionErrorMessage"
      :can-generate="canGenerateContract"
      :can-sync="canSyncContract"
      :can-cancel="canCancelContract"
      @update:pdf-url="contractForm.pdf_url = $event"
      @update:template-id="contractForm.template_id = $event"
      @update:cancel-reason="contractCancelReason = $event"
      @generate="performContractAction('generate')"
      @sync="performContractAction('sync')"
      @cancel="performContractAction('cancel')"
      @open-signer="openContractSignerSignature"
      @close="closeContractDetails"
    />

    <LaisDrawer
      :show="showDetailsModal"
      :title="
        selectedOffer?.status === 'declined' ||
        selectedOffer?.status === 'withdrawn'
          ? 'Proposta cancelada'
          : 'Detalhes da proposta'
      "
      side="right"
      size="2xl"
      @close="closeOfferDetails"
    >
      <div
        v-if="selectedOffer"
        class="offer-details-container min-w-0 max-w-full"
      >
        <div
          data-offer-details-responsive-layout
          class="offer-details-layout grid min-w-0 max-w-full grid-cols-1 gap-4 sm:gap-5"
        >
          <div
            class="offer-details-main grid min-w-0 max-w-full grid-cols-1 gap-4 sm:gap-5"
          >
            <OfferDetailsHeader
              :offer="selectedOffer"
              :buyer-label="buyerLabel(selectedOffer)"
              @profile="openClientProfile"
              @chat="openClientChat"
            />

            <OfferDetailsAlert :offer="selectedOffer" />

            <section
              v-if="isAdmin"
              class="offer-details-inline-action flex flex-col gap-3 rounded-lais border border-lais-primary/20 bg-lais-primary-50/40 p-4"
            >
              <div class="min-w-0">
                <p class="text-[13px] font-semibold text-lais-text">
                  Automação da proposta
                </p>
                <p
                  class="mt-0.5 text-[12px] leading-relaxed text-lais-text-muted"
                >
                  Crie uma regra central para o próximo evento desta negociação.
                </p>
              </div>
              <LaisPrimaryButton
                class="offer-details-inline-action__button w-full shrink-0"
                size="sm"
                variant="ghost"
                icon="i-lucide-workflow"
                @click="createAutomationForOffer(selectedOffer)"
              >
                Criar regra
              </LaisPrimaryButton>
            </section>

            <section
              class="min-w-0 rounded-lais border border-lais-border bg-white p-4 sm:p-5"
            >
              <div
                class="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1"
              >
                <div>
                  <p
                    class="text-[11px] font-semibold uppercase tracking-[0.14em] text-lais-text-muted"
                  >
                    Visão geral
                  </p>
                  <h3
                    class="mt-1 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
                  >
                    Detalhes da proposta
                  </h3>
                </div>
                <span class="text-[12px] text-lais-text-muted">
                  Consulte valores, imóvel e condições.
                </span>
              </div>
              <OfferOverview :offer="selectedOffer" :account-id="accountId" />
            </section>

            <PublicLinkTokenPanel
              :offer="selectedOffer"
              :is-loading="Boolean(publicLinkAction)"
              :error="publicLinkError"
              @revoke="revokeSelectedPublicLink"
              @reissue="reissueSelectedPublicLink"
              @clear-error="publicLinkError = ''"
            />

            <section
              class="offer-details-inline-action flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
            >
              <div class="min-w-0">
                <p class="text-[13px] font-semibold text-lais-text">
                  Compartilhar proposta
                </p>
                <p
                  class="mt-0.5 text-[12px] leading-relaxed text-lais-text-muted"
                >
                  Registra o envio na auditoria e abre o link recebido pelo
                  cliente.
                </p>
              </div>
              <LaisPrimaryButton
                class="offer-details-inline-action__button w-full shrink-0"
                variant="ghost"
                :is-loading="Boolean(offerLinkAction)"
                @click="sendOfferLinkToClient(selectedOffer)"
              >
                {{
                  offerLinkAction
                    ? 'Registrando envio...'
                    : 'Enviar link da proposta'
                }}
              </LaisPrimaryButton>
            </section>

            <section
              class="min-w-0 max-w-full rounded-lais border border-lais-border bg-white p-4"
            >
              <h3
                class="mb-3 font-redHatDisplay text-[16px] font-semibold text-lais-text"
              >
                Andamento da proposta
              </h3>
              <OfferStatusList
                :offer="selectedOffer"
                :offer-id="selectedOffer.id"
                :sales-flow-id="selectedOffer.sales_flow_id"
                @error-refetch="handleOfferStatusListErrorRefetch"
              />
            </section>

            <OfferSummaryCard
              :reference="String(offerReference(selectedOffer))"
              :imovel-titulo="selectedOffer.imovel_titulo || ''"
              :subtitle="
                selectedOffer.imovel_endereco ||
                contextLabel(selectedOffer.business_context)
              "
              :status-label="statusLabel(selectedOffer.status)"
              :status-class="statusClass(selectedOffer.status)"
              :amount-label="offerAmountLabel(selectedOffer)"
              :canceled-reason="selectedOfferCanceledReason"
              :accepted-summary="selectedOfferAcceptedSummary"
            />

            <div
              class="offer-details-financial-grid grid min-w-0 max-w-full grid-cols-1 gap-4"
            >
              <OfferDetailsValuesTable
                :selected-offer="selectedOffer"
                :selected-offer-value-rows="selectedOfferValueRows"
              />

              <OfferDetailsSolicitations
                :selected-offer="selectedOffer"
                :selected-offer-solicitations="selectedOfferSolicitations"
              />
            </div>

            <OfferOperationalActivityPanel
              :context="selectedOffer.operational_context"
              :loading="operationalContextLoading"
              :error-message="operationalContextError"
              @retry="refreshSelectedOfferDeepState({ silent: false })"
            />

            <OfferDetailsContractSection
              v-if="selectedOffer.status === 'accepted' || selectedContract"
              v-model:pdf-url="contractForm.pdf_url"
              v-model:template-id="contractForm.template_id"
              :selected-offer="selectedOffer"
              :selected-contract="selectedContract"
              :selected-contract-provider-status="
                selectedContractProviderStatus
              "
              :contract-action="contractAction"
              :can-generate-contract="canGenerateContract"
              :can-sync-contract="canSyncContract"
              :can-cancel-contract="canCancelContract"
              @generate="performContractAction('generate')"
              @sync="performContractAction('sync')"
              @open-cancel="
                openContractDetails(selectedOffer, {
                  source: 'offer_details_contract_cancel',
                })
              "
              @open-signer="openContractSignerSignature"
            />

            <KycDiligenceChecklistPanel
              v-if="showDiligencePanel"
              data-offer-panel="diligence"
              :gate="selectedDiligenceGate"
              :loading="diligenceLoading"
              :review-loading="diligenceReviewInFlight"
              :error-message="diligenceReviewError"
              :reference-now="referenceNow"
              @review="handleDiligenceReview"
            />

            <TransactionChecklistPanel
              v-if="showTransactionChecklist"
              data-offer-panel="transaction-checklist"
              :transaction="selectedTransaction"
              :offer="selectedOffer"
              :loading="transactionLoading"
              :action-in-flight="checklistActionInFlight"
              :action-error="checklistActionError"
              :reference-now="referenceNow"
              @item-action="handleChecklistItemAction"
            />

            <ReservationHoldPanel
              v-if="selectedReservationHold"
              data-offer-panel="reservation"
              :hold="selectedReservationHold"
              :loading="transactionLoading"
              :unavailable="reservationUnavailable"
              :can-manage="Boolean(selectedOffer)"
              :action-in-flight="reservationActionInFlight"
              :error="reservationError"
              @confirm="confirmSelectedReservation"
              @release="releaseSelectedReservation"
              @cancel="cancelSelectedReservation"
              @refund="refundSelectedReservation"
            />
          </div>

          <aside
            class="offer-details-aside grid min-w-0 max-w-full grid-cols-1 content-start gap-4"
          >
            <div class="offer-details-aside__sticky min-w-0">
              <OfferDetailsPresentationCards
                :selected-offer="selectedOffer"
                @chat="openClientChat"
              />
            </div>

            <OfferKycPanel
              v-if="
                selectedOffer.status === 'accepted' || selectedKycVerification
              "
              v-model:document-type="kycForm.document_type"
              v-model:consent-accepted="kycForm.consent_accepted"
              data-offer-panel="kyc"
              :selected-kyc-verification="selectedKycVerification"
              :selected-kyc-verifications="selectedKycVerifications"
              :selected-active-kyc-verification="selectedActiveKycVerification"
              :selected-kyc-provider-error="selectedKycProviderError"
              :can-submit-kyc-verification="canSubmitKycVerification"
              :kyc-active-attempt-message="kycActiveAttemptMessage"
              :kyc-submit-label="kycSubmitLabel"
              :kyc-action="kycAction"
              :show-kyc-capture="showKycCapture"
              :kyc-capture-payload="kycCapturePayload"
              @toggle-capture="showKycCapture = !showKycCapture"
              @captured="handleKycCaptured"
              @failed="handleKycCaptureFailure"
              @unsupported="handleKycUnsupported"
              @refresh="refreshKycVerifications"
              @submit="submitKycVerification"
            />

            <OfferProgressTimeline
              :selected-offer-steps="selectedOfferSteps"
              :selected-offer-progress-alert="selectedOfferProgressAlert"
              @step-action="handleProgressStepAction"
            />

            <OfferPartnersCard
              v-if="!isRentalOffer(selectedOffer)"
              :selected-offer="selectedOffer"
            />
          </aside>
        </div>
      </div>

      <template #footer>
        <OfferDetailsFooterActions
          v-if="selectedOffer"
          :selected-offer-decision-blocked-reason="
            selectedOfferDecisionBlockedReason
          "
          :offer-action="offerAction"
          :can-edit="canEditOffer(selectedOffer)"
          :can-counter="canCounterOffer(selectedOffer)"
          :can-withdraw="canWithdrawOffer(selectedOffer)"
          :can-decline="canDeclineOffer(selectedOffer)"
          :can-accept="canAcceptOffer(selectedOffer)"
          @close="closeOfferDetails"
          @edit="openEditModal"
          @counter="openCounterModal"
          @withdraw="performOfferAction('withdraw')"
          @decline="openDeclineModal"
          @accept="openAcceptModal"
        />
      </template>
    </LaisDrawer>

    <OfferDecisionModals
      v-model:decline-form="declineForm"
      :accept-modal-title="acceptModalTitle"
      :accept-value-rows="acceptValueRows"
      :accepted-feedback-description="acceptedFeedbackDescription"
      :context-label="contextLabel"
      :created-offer="createdOffer"
      :decline-notes="declineNotes"
      :decline-reason-options="declineReasonOptionsForSelectedOffer"
      :decline-submit-label="declineSubmitLabel"
      :declined-feedback-description="declinedFeedbackDescription"
      :format-b-r-l="formatBRL"
      :kind-label="kindLabel"
      :offer-action="offerAction"
      :offer-summary="offerSummary"
      :selected-offer="selectedOffer"
      :show-accept="showAcceptModal"
      :show-accepted-feedback="showAcceptedFeedbackModal"
      :show-create-success="showCreateSuccessModal"
      :show-decline="showDeclineModal"
      :show-declined-feedback="showDeclinedFeedbackModal"
      @accept="performOfferAction('accept')"
      @close-accept="closeAcceptModal"
      @close-accepted-feedback="closeAcceptedFeedbackModal"
      @close-create-success="closeCreateSuccessModal"
      @close-decline="closeDeclineModal"
      @close-declined-feedback="closeDeclinedFeedbackModal"
      @decline="performOfferAction('decline', $event)"
      @open-created-offer-details="openCreatedOfferDetails"
      @show-accepted-offers="showAcceptedOffers"
      @show-canceled-offers="showCanceledOffers"
    />

    <OfferCounterModal
      :show="showCounterModal"
      :offer="selectedOffer"
      :expected-revision-id="counterExpectedRevisionId(selectedOffer)"
      :is-submitting="offerAction === 'counter'"
      :conflict="counterConflict"
      @close="closeCounterModal"
      @reload="reloadCounterOffer"
      @submit="performCounterOffer"
    />

    <OfferEditModal
      :error="editError"
      :offer="selectedOffer"
      :payment-options="paymentOptions"
      :saving="offerAction === 'edit'"
      :show="showEditModal"
      @close="closeEditModal"
      @submit="updateSelectedOffer"
    />

    <LaisModal
      :show="showCreateModal"
      title="Nova proposta"
      max-width-class="max-w-[860px]"
      @close="closeCreateModal"
    >
      <form class="grid gap-5" @submit.prevent="createProposal">
        <OfferCreateStepper
          :create-step="createStep"
          :create-step-index="createStepIndex"
          :create-steps="createSteps"
          @go-to-step="goToCreateStep"
        />

        <OfferCreatePropertyStep
          v-if="createStep === 'property'"
          v-model:catalog-query="catalogQuery"
          v-model:selected-catalog-item="selectedCatalogItem"
          v-model:selected-catalog-mode="selectedCatalogMode"
          :property-materializing="propertyMaterializing"
          @select-catalog-search-result="selectCatalogSearchResult"
        />

        <OfferCreateBuyerStep
          v-else-if="createStep === 'buyer'"
          v-model:proposal-form="proposalForm"
          v-model:requester-contact-query="requesterContactQuery"
          v-model:selected-requester-contact="selectedRequesterContact"
          @select-requester-contact="selectRequesterContact"
        />

        <OfferCreateValuesStep
          v-else-if="createStep === 'values'"
          v-model:proposal-form="proposalForm"
          :account-id="accountId"
          :custom-solicitation-type-options="customSolicitationTypeOptions"
          :financing-installment-label="financingInstallmentLabel"
          :format-form-b-r-l="formatFormBRL"
          :handle-mortgage-link-click="handleMortgageLinkClick"
          :is-financing-payment="isFinancingPayment"
          :is-financing-proposal-payment="isFinancingProposalPayment"
          :is-sfh-eligible-for-proposal="isSfhEligibleForProposal"
          :payment-breakdown-difference="paymentBreakdownDifference"
          :payment-breakdown-hint="paymentBreakdownHint"
          :payment-needs-fgts="paymentNeedsFgts"
          :payment-options="paymentOptions"
          :payment-recalculating="paymentRecalculating"
          :purchase-payment-rows="purchasePaymentRows"
          :recalculate-payment-fees="recalculatePaymentFees"
          :rental-owner-receive-value="rentalOwnerReceiveValue"
          :schedule-payment-breakdown-defaults="
            schedulePaymentBreakdownDefaults
          "
          :selected-catalog-imovel-id="selectedCatalogImovelId"
          :sfh-limit-label="sfhLimitLabel"
          :show-sfh-option="showSfhOption"
          :sync-payment-breakdown-defaults-now="syncPaymentBreakdownDefaultsNow"
        />

        <OfferCreateReviewSection
          v-else
          :context-label="contextLabel"
          :format-currency="formatBRL"
          :format-form-currency="formatFormBRL"
          :is-financing-payment="isFinancingPayment"
          :payment-label="paymentLabel"
          :payment-needs-fgts="paymentNeedsFgts"
          :proposal-deadline-preview="proposalDeadlinePreview"
          :proposal-form="proposalForm"
          :purchase-payment-info="purchasePaymentInfo"
          :rental-admin-fee-percentage="rentalAdminFeePercentage"
          :rental-admin-fee-value="rentalAdminFeeValue"
          :rental-owner-receive-value="rentalOwnerReceiveValue"
          :rental-residents-situation="rentalResidentsSituation"
          :selected-catalog-summary="selectedCatalogSummary"
          @open-selected-catalog-link="openSelectedCatalogLink"
        />
      </form>

      <template #footer>
        <div
          class="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between"
        >
          <LaisPrimaryButton variant="ghost" @click="closeCreateModal">
            Cancelar
          </LaisPrimaryButton>
          <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <LaisPrimaryButton
              v-if="canGoBackCreateStep"
              variant="ghost"
              icon="i-lucide-chevron-left"
              @click="previousCreateStep"
            >
              Voltar
            </LaisPrimaryButton>
            <LaisPrimaryButton
              v-if="!isReviewStep"
              icon="i-lucide-chevron-right"
              @click="nextCreateStep"
            >
              Continuar
            </LaisPrimaryButton>
            <LaisPrimaryButton
              v-else
              icon="i-lucide-send"
              :is-loading="saving"
              @click="createProposal"
            >
              Criar proposta
            </LaisPrimaryButton>
          </div>
        </div>
      </template>
    </LaisModal>
  </LaisGamificationSurface>
</template>

<style scoped>
.offer-details-container {
  container-type: inline-size;
}

@container (min-width: 32rem) {
  .offer-details-inline-action {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }

  .offer-details-inline-action__button {
    width: auto;
  }
}

@container (min-width: 36rem) {
  .offer-details-main {
    gap: 1.25rem;
  }
}

@container (min-width: 56rem) {
  .offer-details-layout {
    grid-template-columns: minmax(0, 1fr) minmax(19rem, 22rem);
    gap: 1.25rem;
  }

  .offer-details-aside__sticky {
    position: sticky;
    top: 0;
    align-self: start;
  }
}

@container (min-width: 64rem) {
  .offer-details-layout {
    grid-template-columns: minmax(0, 1fr) minmax(22rem, 25rem);
    gap: 1.5rem;
  }

  .offer-details-aside__sticky {
    position: sticky;
    top: 0;
    align-self: start;
  }
}

@container (min-width: 76rem) {
  .offer-details-layout {
    grid-template-columns: minmax(0, 1fr) minmax(25rem, 30rem);
    gap: 2rem;
  }

  .offer-details-main {
    gap: 1.5rem;
  }

  .offer-details-financial-grid {
    align-items: start;
    grid-template-columns: minmax(0, 1.35fr) minmax(19rem, 0.85fr);
  }
}

@container (min-width: 84rem) {
  .offer-details-layout {
    grid-template-columns: minmax(0, 1fr) minmax(27rem, 32rem);
    gap: 2.25rem;
  }
}
</style>
